<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { fly, fade, slide } from "svelte/transition";
  import { fetchTrucks, createTruck, updateTruck, createTrailer, fetchTrailers, fetchYards, fetchJobs, TRUCK_CONFIGS, TRAILER_CONFIGS } from "../../data.js";
  import TurnaroundSchedulePanel from "../Job/TurnaroundSchedulePanel.svelte";
  import DeliveryTruck from "carbon-icons-svelte/lib/DeliveryTruck.svelte";
  import Calendar from "carbon-icons-svelte/lib/Calendar.svelte";
  import Time from "carbon-icons-svelte/lib/Time.svelte";
  import Location from "carbon-icons-svelte/lib/Location.svelte";
  import Enterprise from "carbon-icons-svelte/lib/Enterprise.svelte";
  import ArrowRight from "carbon-icons-svelte/lib/ArrowRight.svelte";
  import Email from "carbon-icons-svelte/lib/Email.svelte";
  import Add from "carbon-icons-svelte/lib/Add.svelte";
  import TrashCan from "carbon-icons-svelte/lib/TrashCan.svelte";
  import Close from "carbon-icons-svelte/lib/Close.svelte";
  import ChevronLeft from "carbon-icons-svelte/lib/ChevronLeft.svelte";
  import ChevronRight from "carbon-icons-svelte/lib/ChevronRight.svelte";
  import ChevronUp from "carbon-icons-svelte/lib/ChevronUp.svelte";
  import ChevronDown from "carbon-icons-svelte/lib/ChevronDown.svelte";
  import Search from "carbon-icons-svelte/lib/Search.svelte";
  import Checkmark from "carbon-icons-svelte/lib/Checkmark.svelte";
  import Layers from "carbon-icons-svelte/lib/Layers.svelte";
  import Draggable from "carbon-icons-svelte/lib/Draggable.svelte";
  import User from "carbon-icons-svelte/lib/User.svelte";
  import Phone from "carbon-icons-svelte/lib/Phone.svelte";
  import Edit from "carbon-icons-svelte/lib/Edit.svelte";
  import WarningFilled from "carbon-icons-svelte/lib/WarningFilled.svelte";
  import Launch from "carbon-icons-svelte/lib/Launch.svelte";
  import Copy from "carbon-icons-svelte/lib/Copy.svelte";
  import PhoneInput from "../../components/PhoneInput.svelte";
  import { formatPhoneNumber, toE164 } from "../../phoneUtils.js";
  import { yards } from "../../stores.js";
  import { reportYardImageError, getYardName, validateFullAddress, inlineAddress, parseMapboxAddress } from "../../helpers.js";
  import { Geocoder } from "@beyonk/svelte-mapbox";
  import { calculateScheduledDate, getJobCustomerName } from "./jobMaterialUtils.js";
  import { getYardLiveUrl, getClientJobLiveUrl, copyToClipboard } from "../../liveTrackingUtils.js";

  export let materials = [];
  export let startDate = new Date().toISOString().split("T")[0];
  export let selectedMaterial = null;
  export let jobs = [];
  export let currentJobId = null;
  export let currentJob = null;

  let previousJobId = undefined;
  $: if (currentJobId !== previousJobId) {
    previousJobId = currentJobId;
    allocations = {};
    lastHydratedSignature = {};
    clearConflictAlert();
  }

  let internalJobs = [];
  $: effectiveJobs = (Array.isArray(jobs) && jobs.length > 0) ? jobs : internalJobs;

  const dispatch = createEventDispatcher();

  export let fleetTrucks = [];
  let isLoadingFleet = false;
  export let isFleetDrawerOpen = false;
  let searchTruckQuery = "";
  let truckTypeFilter = "all"; // 'all' | '6 wheeler' | '8 wheeler' | '10 wheeler'

  // Add truck state & form fields
  let isAddingTruck = false;
  let isSavingTruck = false;
  let addTruckError = "";
  let recentlyAddedTruckId = null;

  let newTruckId = "";
  let newTruckType = "8 wheeler";
  let newTruckCapacity = 17;
  let newTruckDriverName = "";
  let newTruckDriverPhone = "";

  // Trailer toggle & fields
  let attachTrailer = false;
  let newTrailerId = "";
  let newTrailerType = "8 wheeler";
  let newTrailerCapacity = 20;

  function handleNewTruckTypeChange(type) {
    newTruckType = type;
    if (TRUCK_CONFIGS[type]) {
      newTruckCapacity = TRUCK_CONFIGS[type].defaultCapacity;
    }
  }

  function handleNewTrailerTypeChange(type) {
    newTrailerType = type;
    if (TRAILER_CONFIGS[type]) {
      newTrailerCapacity = TRAILER_CONFIGS[type].defaultCapacity;
    }
  }

  function resetAddTruckForm() {
    isAddingTruck = false;
    isSavingTruck = false;
    addTruckError = "";
    newTruckId = "";
    newTruckType = "8 wheeler";
    newTruckCapacity = 17;
    newTruckDriverName = "";
    newTruckDriverPhone = "";
    attachTrailer = false;
    newTrailerId = "";
    newTrailerType = "8 wheeler";
    newTrailerCapacity = 20;
  }

  async function handleAddTruckSubmit() {
    const rego = (newTruckId || "").trim().toUpperCase();
    if (!rego) {
      addTruckError = "Truck ID or Registration plate is required.";
      return;
    }

    if (fleetTrucks.some(t => String(t.id).toUpperCase() === rego)) {
      addTruckError = `A truck with ID "${rego}" already exists in the fleet.`;
      return;
    }

    let trailerPayload = undefined;
    if (attachTrailer) {
      const trlRego = (newTrailerId || "").trim().toUpperCase();
      if (!trlRego) {
        addTruckError = "Trailer registration plate number is required when attaching a trailer.";
        return;
      }
      if (trlRego === rego) {
        addTruckError = "Trailer registration plate cannot be identical to the truck registration plate.";
        return;
      }

      // Check trailer exclusivity across existing fleet
      const conflictTruck = fleetTrucks.find(t => {
        const trl = t.default_trailer || t.trailer;
        return trl && String(trl.id).toUpperCase() === trlRego && String(t.id).toUpperCase() !== rego;
      });
      if (conflictTruck) {
        addTruckError = `Trailer "${trlRego}" is already attached to truck "${conflictTruck.id}". A trailer can only be attached to one truck at a time.`;
        return;
      }

      trailerPayload = {
        id: trlRego,
        type: newTrailerType,
        capacity: Number(newTrailerCapacity) || TRAILER_CONFIGS[newTrailerType]?.defaultCapacity || 20
      };
    }

    isSavingTruck = true;
    addTruckError = "";

    try {
      const truckPayload = {
        id: rego,
        type: newTruckType,
        capacity: Number(newTruckCapacity) || TRUCK_CONFIGS[newTruckType]?.defaultCapacity || 17,
        default_driver: newTruckDriverName.trim() ? {
          name: newTruckDriverName.trim(),
          phone: newTruckDriverPhone.trim() ? toE164(newTruckDriverPhone.trim()) : undefined
        } : undefined,
        default_trailer: trailerPayload,
        trailer: trailerPayload
      };

      const savedTruck = await createTruck(truckPayload);

      // Prepend to fleetTrucks so it is right at the top of the fleet list
      fleetTrucks = [savedTruck, ...fleetTrucks.filter(t => String(t.id).toUpperCase() !== rego)];

      // Reset filters so the new truck is immediately visible
      searchTruckQuery = "";
      if (truckTypeFilter !== "all" && truckTypeFilter !== savedTruck.type) {
        truckTypeFilter = "all";
      }

      recentlyAddedTruckId = rego;
      setTimeout(() => {
        if (recentlyAddedTruckId === rego) recentlyAddedTruckId = null;
      }, 5000);

      resetAddTruckForm();
    } catch (err) {
      console.error("Error creating truck:", err);
      addTruckError = err.message || "Failed to add truck. Please try again.";
    } finally {
      isSavingTruck = false;
    }
  }

  // Truck Detail Modal state (for clicking a truck in a day column or in the fleet drawer)
  let isDetailModalOpen = false;
  let detailModalMode = 'allocation'; // 'allocation' | 'truck-default'
  let detailTruckTarget = null;
  let detailDayIndex = null;
  let detailTruckIndex = null;
  let detailAllocationItem = null;

  // Editable fields in modal:
  let editDriverName = "";
  let editDriverPhone = "";
  let editTrailer = null; // null | { id, type, capacity }
  let trailerEditMode = "none"; // 'none' | 'existing' | 'new'
  let selectedExistingTrailerId = "";
  let newDetailTrailerId = "";
  let newDetailTrailerType = "8 wheeler";
  let newDetailTrailerCapacity = 20;
  let detailModalError = "";
  let availableTrailersList = [];
  let isSavingDetail = false;

  async function openTruckDetailModal(dayIndex, truckIdx, item) {
    detailModalMode = 'allocation';
    detailTruckTarget = null;
    detailDayIndex = dayIndex;
    detailTruckIndex = truckIdx;
    detailAllocationItem = item;
    detailModalError = "";

    // Driver:
    editDriverName = item.driver?.name || item.truck?.default_driver?.name || "";
    editDriverPhone = formatPhoneNumber(item.driver?.phone || item.truck?.default_driver?.phone || "");

    // Trailer (from allocation or truck):
    editTrailer = item.trailer ? { ...item.trailer } : ((item.truck?.default_trailer || item.truck?.trailer) ? { ...(item.truck.default_trailer || item.truck.trailer) } : null);

    trailerEditMode = "none";
    selectedExistingTrailerId = "";
    newDetailTrailerId = "";
    newDetailTrailerType = "8 wheeler";
    newDetailTrailerCapacity = 20;

    // Fetch existing trailers to populate dropdown
    try {
      const trailers = await fetchTrailers();
      availableTrailersList = Array.isArray(trailers) ? trailers : [];
    } catch (_) {
      availableTrailersList = [];
    }

    isDetailModalOpen = true;
  }

  async function openTruckDefaultConfigModal(truck) {
    if (!truck) return;
    detailModalMode = 'truck-default';
    detailTruckTarget = truck;
    detailDayIndex = null;
    detailTruckIndex = null;
    detailModalError = "";

    const trl = truck.default_trailer || truck.trailer;
    detailAllocationItem = {
      truck: { ...truck },
      trailer: trl ? { ...trl } : null,
      driver: truck.default_driver ? { ...truck.default_driver } : null
    };

    // Driver:
    editDriverName = truck.default_driver?.name || "";
    editDriverPhone = formatPhoneNumber(truck.default_driver?.phone || "");

    // Trailer:
    editTrailer = trl ? { ...trl } : null;

    trailerEditMode = "none";
    selectedExistingTrailerId = "";
    newDetailTrailerId = "";
    newDetailTrailerType = "8 wheeler";
    newDetailTrailerCapacity = 20;

    // Fetch existing trailers to populate dropdown
    try {
      const trailers = await fetchTrailers();
      availableTrailersList = Array.isArray(trailers) ? trailers : [];
    } catch (_) {
      availableTrailersList = [];
    }

    isDetailModalOpen = true;
  }

  async function saveTruckDetailModal() {
    if (detailModalMode === 'truck-default') {
      await saveTruckDefaultModal();
      return;
    }

    if (!detailAllocationItem) {
      isDetailModalOpen = false;
      return;
    }

    detailModalError = "";

    // Robust schedule resolution
    let key = selectedMaterial ? getMaterialKey(selectedMaterial) : null;
    let current = key ? allocations[key] : null;

    if (!current || !current.days) {
      for (const [k, sched] of Object.entries(allocations || {})) {
        if (sched?.days) {
          for (const [dIdx, list] of Object.entries(sched.days)) {
            if (Array.isArray(list) && list.some(it => 
              it === detailAllocationItem || 
              String(it.truck?.id || "").toUpperCase() === String(detailAllocationItem?.truck?.id || "").toUpperCase()
            )) {
              key = k;
              current = sched;
              if (detailDayIndex === null) detailDayIndex = parseInt(dIdx, 10);
              break;
            }
          }
        }
        if (current) break;
      }
    }

    if (!current && allocations && Object.keys(allocations).length === 1) {
      key = Object.keys(allocations)[0];
      current = Object.values(allocations)[0];
    }

    if (!current) {
      if (selectedMaterial) {
        key = getMaterialKey(selectedMaterial);
        initScheduleForMaterial(selectedMaterial);
        current = allocations[key];
      }
    }

    if (!current) {
      current = { days: {}, startTime: "07:00", loadInterval: 15, daysCount: 1 };
      if (key) allocations[key] = current;
    }
    if (!current.days) current.days = {};

    // Resolve day index
    if (detailDayIndex === null) {
      for (const [dIdx, list] of Object.entries(current.days)) {
        if (Array.isArray(list) && list.some(it => 
          it === detailAllocationItem || 
          String(it.truck?.id || "").toUpperCase() === String(detailAllocationItem?.truck?.id || "").toUpperCase()
        )) {
          detailDayIndex = parseInt(dIdx, 10);
          break;
        }
      }
    }
    if (detailDayIndex === null) detailDayIndex = 0;
    if (!current.days[detailDayIndex]) current.days[detailDayIndex] = [];

    // Handle new trailer creation if user filled the new trailer form
    let finalTrailer = editTrailer;
    if (trailerEditMode === "new") {
      const trlRego = (newDetailTrailerId || "").trim().toUpperCase();
      if (!trlRego) {
        detailModalError = "Please enter a registration plate for the new trailer, or click Cancel.";
        return;
      }
      if (trlRego === String(detailAllocationItem.truck?.id).toUpperCase()) {
        detailModalError = "Trailer plate cannot be the same as the truck registration plate.";
        return;
      }
      // Check exclusivity against fleetTrucks and currently scheduled trucks in other slots
      const conflictTruck = fleetTrucks.find(t => {
        const trl = t.default_trailer || t.trailer;
        return trl && String(trl.id).toUpperCase() === trlRego && String(t.id).toUpperCase() !== String(detailAllocationItem.truck?.id).toUpperCase();
      });
      if (conflictTruck) {
        detailModalError = `Trailer "${trlRego}" is already attached to truck "${conflictTruck.id}". A trailer can only be attached to one truck at a time.`;
        return;
      }

      finalTrailer = {
        id: trlRego,
        type: newDetailTrailerType,
        capacity: Number(newDetailTrailerCapacity) || TRAILER_CONFIGS[newDetailTrailerType]?.defaultCapacity || 20,
        attached_to_truck_id: detailAllocationItem.truck?.id
      };

      try {
        await createTrailer(finalTrailer);
      } catch (e) {
        console.warn("Could not save trailer to backend (offline/local fallback):", e);
      }
    } else if (trailerEditMode === "existing") {
      if (!selectedExistingTrailerId) {
        detailModalError = "Please select a trailer from the list, or click Cancel.";
        return;
      }
      const found = availableTrailersList.find(t => String(t.id).toUpperCase() === String(selectedExistingTrailerId).toUpperCase());
      if (found) {
        const conflictTruck = fleetTrucks.find(t => {
          const trl = t.default_trailer || t.trailer;
          return trl && String(trl.id).toUpperCase() === String(found.id).toUpperCase() && String(t.id).toUpperCase() !== String(detailAllocationItem.truck?.id).toUpperCase();
        });
        if (conflictTruck || (found.attached_to_truck_id && String(found.attached_to_truck_id).toUpperCase() !== String(detailAllocationItem.truck?.id).toUpperCase())) {
          const conflictName = conflictTruck ? conflictTruck.id : found.attached_to_truck_id;
          detailModalError = `Trailer "${found.id}" is already attached to truck "${conflictName}". A trailer can only be attached to one truck at a time.`;
          return;
        }
        finalTrailer = {
          id: found.id,
          type: found.type,
          capacity: found.capacity || TRAILER_CONFIGS[found.type]?.defaultCapacity || 20,
          attached_to_truck_id: detailAllocationItem.truck?.id
        };
      }
    }

    // Exclusivity check on finalTrailer across this day's other trucks
    if (finalTrailer && finalTrailer.id) {
      const currentDayTrucks = current.days[detailDayIndex] || [];
      const duplicateInDay = currentDayTrucks.find((item, idx) => {
        if (idx === detailTruckIndex) return false;
        const itemTrlId = item.trailer?.id || item.truck?.default_trailer?.id || item.truck?.trailer?.id;
        return itemTrlId && itemTrlId.toUpperCase() === finalTrailer.id.toUpperCase();
      });
      if (duplicateInDay) {
        detailModalError = `Trailer "${finalTrailer.id}" is already allocated to truck "${duplicateInDay.truck?.id}" on this day.`;
        return;
      }
    }

    isSavingDetail = true;

    try {
      const truckId = String(detailAllocationItem.truck?.id || "").trim().toUpperCase();
      const driverName = editDriverName.trim();
      const driverPhone = editDriverPhone.trim();
      const driverObj = {
        name: driverName || `Driver ${truckId || ""}`.trim(),
        phone: driverPhone ? toE164(driverPhone) : undefined
      };

      const truckBaseCap = Number(detailAllocationItem.truck?.capacity || 17);
      const trailerCap = Number(finalTrailer?.capacity || 0);
      const totalAllocCap = truckBaseCap + trailerCap;

      // 1. Resolve target item index in current.days[detailDayIndex]
      const dayList = current.days[detailDayIndex] || [];
      let targetItemIndex = -1;
      if (Array.isArray(dayList)) {
        targetItemIndex = dayList.findIndex(it => 
          it === detailAllocationItem || 
          (truckId && String(it.truck?.id || "").toUpperCase() === truckId)
        );
      }
      if (targetItemIndex === -1 && detailTruckIndex != null && dayList[detailTruckIndex]) {
        targetItemIndex = detailTruckIndex;
      }

      const updatedItemPayload = {
        driver: { ...driverObj },
        trailer: finalTrailer ? { ...finalTrailer } : null,
        scheduledStartTime: dayList[targetItemIndex]?.scheduledStartTime || detailAllocationItem?.scheduledStartTime || current.startTime || "07:00"
      };

      // 2. Immutably update the day's truck array for reactivity
      if (targetItemIndex >= 0 && targetItemIndex < dayList.length) {
        current.days[detailDayIndex] = dayList.map((it, idx) => {
          if (idx === targetItemIndex || it === detailAllocationItem || (truckId && String(it.truck?.id || "").toUpperCase() === truckId)) {
            return {
              ...it,
              ...updatedItemPayload,
              truck: {
                ...it.truck,
                default_driver: { ...driverObj },
                trailer: finalTrailer ? { ...finalTrailer } : null,
                total_capacity: totalAllocCap
              }
            };
          }
          return it;
        });
      } else {
        const newItem = {
          ...(detailAllocationItem || {}),
          ...updatedItemPayload,
          truck: {
            ...(detailAllocationItem?.truck || { id: truckId, type: "8 wheeler", capacity: truckBaseCap }),
            default_driver: { ...driverObj },
            trailer: finalTrailer ? { ...finalTrailer } : null,
            total_capacity: totalAllocCap
          }
        };
        current.days[detailDayIndex] = [...dayList, newItem];
      }

      // Also directly update detailAllocationItem reference
      if (detailAllocationItem) {
        detailAllocationItem.driver = { ...driverObj };
        detailAllocationItem.trailer = finalTrailer ? { ...finalTrailer } : null;
        if (detailAllocationItem.truck) {
          detailAllocationItem.truck.default_driver = { ...driverObj };
          detailAllocationItem.truck.default_trailer = finalTrailer ? { ...finalTrailer } : null;
          detailAllocationItem.truck.trailer = finalTrailer ? { ...finalTrailer } : null;
          detailAllocationItem.truck.total_capacity = totalAllocCap;
        }
      }

      // 3. Update fleetTruck in fleetTrucks store and persist to backend
      if (truckId) {
        const fleetIdx = fleetTrucks.findIndex(t => String(t.id).toUpperCase() === truckId);
        if (fleetIdx !== -1) {
          const fleetTruck = { ...fleetTrucks[fleetIdx] };
          fleetTruck.default_driver = { ...driverObj };
          if (finalTrailer) {
            fleetTruck.default_trailer = { ...finalTrailer };
            fleetTruck.trailer = { ...finalTrailer };
            fleetTruck.total_capacity = (Number(fleetTruck.capacity) || 17) + (Number(finalTrailer.capacity) || 0);
          } else if (trailerEditMode !== 'none') {
            fleetTruck.default_trailer = null;
            fleetTruck.trailer = null;
            fleetTruck.total_capacity = Number(fleetTruck.capacity) || 17;
          }
          fleetTrucks[fleetIdx] = fleetTruck;
          fleetTrucks = [...fleetTrucks];

          try {
            await updateTruck(truckId, {
              default_driver: driverObj,
              default_trailer: fleetTruck.default_trailer,
              trailer: fleetTruck.trailer,
              total_capacity: fleetTruck.total_capacity || truckBaseCap
            });
          } catch (e) {
            console.warn("Could not persist truck default updates to backend:", e);
          }
        }
      }

      // 4. Update allocations store & notify listeners
      current.days = { ...current.days };
      if (key) {
        allocations[key] = { ...current };
      }
      allocations = { ...allocations };
      emitChanges();

      // 5. Persist directly to backend database
      if (typeof onSaveAllocation === "function") {
        try {
          await onSaveAllocation(allocations, detailAllocationItem);
        } catch (saveErr) {
          console.error("Failed to persist allocation to database:", saveErr);
          detailModalError = saveErr.message || "Failed to persist changes to the database. Please try again.";
          return;
        }
      }

      isDetailModalOpen = false;
    } catch (err) {
      console.error("Error saving allocation detail:", err);
      detailModalError = err.message || "Failed to save truck allocation details.";
    } finally {
      isSavingDetail = false;
    }
  }

  async function saveTruckDefaultModal() {
    if (!detailTruckTarget) {
      isDetailModalOpen = false;
      return;
    }
    const truckId = String(detailTruckTarget.id).trim().toUpperCase();
    detailModalError = "";

    // Handle new trailer creation if user filled the new trailer form
    let finalTrailer = editTrailer;
    if (trailerEditMode === "new") {
      const trlRego = (newDetailTrailerId || "").trim().toUpperCase();
      if (!trlRego) {
        detailModalError = "Please enter a registration plate for the new trailer, or click Cancel.";
        return;
      }
      if (trlRego === truckId) {
        detailModalError = "Trailer plate cannot be the same as the truck registration plate.";
        return;
      }
      // Check exclusivity against fleetTrucks
      const conflictTruck = fleetTrucks.find(t => {
        const trl = t.default_trailer || t.trailer;
        return trl && String(trl.id).toUpperCase() === trlRego && String(t.id).toUpperCase() !== truckId;
      });
      if (conflictTruck) {
        detailModalError = `Trailer "${trlRego}" is already attached to truck "${conflictTruck.id}". A trailer can only be attached to one truck at a time.`;
        return;
      }

      finalTrailer = {
        id: trlRego,
        type: newDetailTrailerType,
        capacity: Number(newDetailTrailerCapacity) || TRAILER_CONFIGS[newDetailTrailerType]?.defaultCapacity || 20,
        attached_to_truck_id: truckId
      };

      try {
        await createTrailer(finalTrailer);
      } catch (e) {
        console.warn("Could not save trailer to backend (offline/local fallback):", e);
      }
    } else if (trailerEditMode === "existing") {
      if (!selectedExistingTrailerId) {
        detailModalError = "Please select a trailer from the list, or click Cancel.";
        return;
      }
      const found = availableTrailersList.find(t => String(t.id).toUpperCase() === String(selectedExistingTrailerId).toUpperCase());
      if (found) {
        const conflictTruck = fleetTrucks.find(t => {
          const trl = t.default_trailer || t.trailer;
          return trl && String(trl.id).toUpperCase() === String(found.id).toUpperCase() && String(t.id).toUpperCase() !== truckId;
        });
        if (conflictTruck || (found.attached_to_truck_id && String(found.attached_to_truck_id).toUpperCase() !== truckId)) {
          const conflictName = conflictTruck ? conflictTruck.id : found.attached_to_truck_id;
          detailModalError = `Trailer "${found.id}" is already attached to truck "${conflictName}". A trailer can only be attached to one truck at a time.`;
          return;
        }
        finalTrailer = {
          id: found.id,
          type: found.type,
          capacity: found.capacity || TRAILER_CONFIGS[found.type]?.defaultCapacity || 20,
          attached_to_truck_id: truckId
        };
      }
    }

    isSavingDetail = true;

    try {
      const driverObj = editDriverName.trim() ? {
        name: editDriverName.trim(),
        phone: editDriverPhone.trim() ? toE164(editDriverPhone.trim()) : undefined
      } : null;

      const baseCap = Number(detailTruckTarget.capacity || 17);
      const trailerCap = Number(finalTrailer?.capacity || 0);
      const totalCap = baseCap + trailerCap;

      const truckUpdates = {
        default_driver: driverObj,
        default_trailer: finalTrailer,
        trailer: finalTrailer,
        total_capacity: totalCap
      };

      // 1. Update truck on backend
      try {
        await updateTruck(truckId, truckUpdates);
      } catch (e) {
        console.warn("Could not update truck default on backend:", e);
      }

      // 2. Update local fleetTrucks list
      const fleetIdx = fleetTrucks.findIndex(t => String(t.id).toUpperCase() === truckId);
      if (fleetIdx !== -1) {
        fleetTrucks[fleetIdx] = {
          ...fleetTrucks[fleetIdx],
          ...truckUpdates
        };
        fleetTrucks = [...fleetTrucks];
      }

      // 3. Update any currently scheduled allocations that reference this truck (immutably)
      Object.keys(allocations).forEach(matKey => {
        const sched = allocations[matKey];
        if (sched && sched.days) {
          Object.keys(sched.days).forEach(dIdx => {
            const list = sched.days[dIdx];
            if (Array.isArray(list)) {
              sched.days[dIdx] = list.map(item => {
                if (String(item.truck?.id).toUpperCase() === truckId) {
                  return {
                    ...item,
                    driver: driverObj ? { ...driverObj } : item.driver,
                    trailer: finalTrailer ? { ...finalTrailer } : item.trailer,
                    truck: {
                      ...item.truck,
                      ...truckUpdates
                    }
                  };
                }
                return item;
              });
            }
          });
        }
      });
      allocations = { ...allocations };
      emitChanges();

      // Persist directly to backend database if handler is available
      if (typeof onSaveAllocation === "function") {
        try {
          await onSaveAllocation(allocations);
        } catch (saveErr) {
          console.error("Failed to persist allocation defaults to database:", saveErr);
          detailModalError = saveErr.message || "Failed to persist changes to the database. Please try again.";
          return;
        }
      }

      isDetailModalOpen = false;
    } catch (err) {
      console.error("Error saving truck defaults:", err);
      detailModalError = err.message || "Failed to save truck defaults.";
    } finally {
      isSavingDetail = false;
    }
  }

  // Allocation state per material:
  // { [materialKey]: { daysCount: number, startTime: string, loadInterval: number, days: { [dayIndex]: [ { truck, scheduledStartTime } ] } } }
  export let allocations = {};
  export let onSaveAllocation = null;
  let draggedTruck = null;
  let dragOverDayIndex = null;
  let dragOverConflict = false;

  let allocationConflictAlert = null;
  let conflictAlertTimeout = null;

  onMount(async () => {
    if (!$yards || $yards.length === 0) {
      try {
        await fetchYards();
      } catch (err) {
        console.warn("Failed to load yards in TruckAllocationBoard:", err);
      }
    }
    if (!fleetTrucks || fleetTrucks.length === 0) {
      isLoadingFleet = true;
      try {
        fleetTrucks = await fetchTrucks();
      } catch (err) {
        console.error("Failed to load fleet trucks:", err);
      } finally {
        isLoadingFleet = false;
      }
    }
    if (!jobs || jobs.length === 0) {
      try {
        const fetched = await fetchJobs();
        internalJobs = Array.isArray(fetched) ? fetched : (fetched?.jobs || []);
      } catch (err) {
        console.warn("Failed to load jobs in TruckAllocationBoard:", err);
      }
    }
  });

  // Select first material by default if none selected or if materials switched
  $: if (materials.length > 0 && (!selectedMaterial || !materials.some(m => m === selectedMaterial || getMaterialKey(m) === getMaterialKey(selectedMaterial)))) {
    selectedMaterial = materials[0];
  }

  $: activeTurnaroundMat = selectedMaterial || (Array.isArray(materials) && materials.length > 0 ? materials[0] : null);

  function handleTurnaroundPanelChange(e) {
    const updatedMat = e.detail?.material || activeTurnaroundMat;
    if (updatedMat) {
      if (selectedMaterial && getMaterialKey(selectedMaterial) === getMaterialKey(updatedMat)) {
        selectedMaterial = { ...selectedMaterial, ...updatedMat };
        const key = getMaterialKey(selectedMaterial);
        if (allocations[key]) {
          let needsTimingUpdate = false;
          const newStart = updatedMat.operating_hours?.start_time || updatedMat.start_time;
          if (newStart && allocations[key].startTime !== newStart) {
            allocations[key].startTime = newStart;
            needsTimingUpdate = true;
          }
          const newInterval = updatedMat.operating_hours?.load_mins ?? updatedMat.load_mins;
          if (newInterval != null && allocations[key].loadInterval !== Number(newInterval)) {
            allocations[key].loadInterval = Number(newInterval);
            needsTimingUpdate = true;
          }
          if (needsTimingUpdate) {
            updateTiming();
          }
        }
      }
      if (Array.isArray(materials)) {
        const idx = materials.findIndex(m => getMaterialKey(m) === getMaterialKey(updatedMat));
        if (idx !== -1) {
          materials[idx] = { ...materials[idx], ...updatedMat };
          materials = [...materials];
        }
      }
    }
    dispatch('turnaround-change', e.detail);
  }

  function initScheduleForMaterial(mat, shouldEmit = false) {
    if (!mat) return;
    const key = getMaterialKey(mat);
    const estimatedDays = computeEstimatedDays(mat);
    const defaultStartTime = mat.operating_hours?.start_time || "07:00";
    const defaultLoadInterval = mat.operating_hours?.load_mins != null ? mat.operating_hours.load_mins : 15;
    
    // Check if mat has existing allocated_trucks
    let maxDays = estimatedDays;
    const existingAllocs = Array.isArray(mat.allocated_trucks)
      ? mat.allocated_trucks
      : (Array.isArray(mat.schedule?.allocated_trucks) ? mat.schedule.allocated_trucks : []);
    if (Array.isArray(existingAllocs) && existingAllocs.length > 0) {
      existingAllocs.forEach(tAlloc => {
        const dNum = parseInt(tAlloc.scheduled_day || 1, 10);
        if (dNum > maxDays) maxDays = dNum;
      });
    }

    const initialDays = {};
    for (let i = 0; i < maxDays; i++) {
      initialDays[i] = [];
    }

    if (Array.isArray(existingAllocs) && existingAllocs.length > 0) {
      existingAllocs.forEach(tAlloc => {
        const dayIdx = Math.max(0, parseInt(tAlloc.scheduled_day || 1, 10) - 1);
        if (!initialDays[dayIdx]) initialDays[dayIdx] = [];

        // Hydrate truck details against fleetTrucks
        let truckIdStr = "";
        let truckObj = null;
        if (typeof tAlloc.truck === "object" && tAlloc.truck !== null) {
          truckObj = { ...tAlloc.truck };
          truckIdStr = String(truckObj.id || "").toUpperCase();
        } else if (typeof tAlloc.truck === "string" || typeof tAlloc.truck === "number") {
          truckIdStr = String(tAlloc.truck).toUpperCase();
        }

        const fleetMatch = fleetTrucks.find(t => String(t.id).toUpperCase() === truckIdStr);
        if (fleetMatch) {
          truckObj = { ...fleetMatch, ...truckObj };
        } else if (!truckObj) {
          truckObj = { id: truckIdStr || "TRUCK", type: "8 wheeler", capacity: 17 };
        }

        let driverObj = null;
        if (tAlloc.driver && tAlloc.driver.name && tAlloc.driver.name.trim()) {
          driverObj = { ...tAlloc.driver };
        } else if (truckObj.default_driver && truckObj.default_driver.name && truckObj.default_driver.name.trim()) {
          driverObj = { ...truckObj.default_driver };
        } else {
          driverObj = { name: `Driver ${truckObj.id || ""}`.trim(), phone: "" };
        }

        let trailerObj = null;
        const truckDefaultTrailer = truckObj.default_trailer || truckObj.trailer;
        if (tAlloc.trailer && (tAlloc.trailer.id || tAlloc.trailer.type)) {
          trailerObj = { ...tAlloc.trailer };
        } else if (truckDefaultTrailer && (truckDefaultTrailer.id || truckDefaultTrailer.type)) {
          trailerObj = { ...truckDefaultTrailer };
        }

        const alreadyInDay = initialDays[dayIdx].some(it => String(it.truck?.id).toUpperCase() === truckIdStr);
        if (!alreadyInDay) {
          initialDays[dayIdx].push({
            truck: { ...truckObj },
            trailer: trailerObj ? { ...trailerObj } : null,
            driver: driverObj ? { ...driverObj } : null,
            scheduledStartTime: tAlloc.scheduled_start_time || tAlloc.scheduledStartTime || ""
          });
        }
      });

      // Recalculate start times for populated days
      Object.keys(initialDays).forEach(dIdx => {
        if (initialDays[dIdx].length > 0) {
          initialDays[dIdx] = recalculateDayTimes(initialDays[dIdx], defaultStartTime, defaultLoadInterval);
        }
      });
    }

    // Clean up any stale key for this same material (e.g. keyed by material_name before id was assigned)
    const matNameKey = mat.material_name || mat.name;
    if (matNameKey && String(matNameKey) !== String(key) && allocations[matNameKey]) {
      const otherUsesNameKey = Array.isArray(materials) && materials.some(m => m !== mat && getMaterialKey(m) === String(matNameKey));
      if (!otherUsesNameKey) {
        delete allocations[matNameKey];
      }
    }

    allocations[key] = {
      materialName: mat.material_name || mat.name,
      daysCount: maxDays,
      startTime: defaultStartTime,
      loadInterval: defaultLoadInterval,
      days: initialDays
    };
    allocations = { ...allocations };
    if (shouldEmit) {
      emitChanges();
    }
  }

  function computeAllocationSignature(matKey, trucksList) {
    if (!Array.isArray(trucksList)) return `${matKey}_empty`;
    return `${matKey}_${trucksList.map(t => {
      if (typeof t === 'object' && t !== null) {
        const trkId = t.id || t.truck?.id || t.truck || '';
        const drv = t.driver?.name || t.truck?.default_driver?.name || '';
        const drvPhone = t.driver?.phone || t.truck?.default_driver?.phone || '';
        const trl = t.trailer?.id || t.truck?.default_trailer?.id || t.truck?.trailer?.id || '';
        const time = t.scheduled_start_time || t.scheduledStartTime || '';
        const day = t.scheduled_day || 1;
        return `${trkId}:${drv}:${drvPhone}:${trl}:${day}:${time}`;
      }
      return String(t);
    }).join(',')}`;
  }

  let lastHydratedSignature = {};

  // Ensure current selectedMaterial has an allocation structure and re-hydrates when allocated_trucks arrive from database
  $: if (selectedMaterial) {
    const key = getMaterialKey(selectedMaterial);
    const existingTrucks = Array.isArray(selectedMaterial.allocated_trucks) 
      ? selectedMaterial.allocated_trucks 
      : (Array.isArray(selectedMaterial.schedule?.allocated_trucks) ? selectedMaterial.schedule.allocated_trucks : []);
    
    // Content-based signature of trucks in selectedMaterial
    const signature = computeAllocationSignature(key, existingTrucks);

    if (!allocations[key] || !allocations[key].days) {
      lastHydratedSignature[key] = signature;
      initScheduleForMaterial(selectedMaterial);
    } else if (lastHydratedSignature[key] !== signature && existingTrucks.length > 0) {
      // Async database load completed after initial empty mount: re-hydrate the board with the trucks!
      lastHydratedSignature[key] = signature;
      initScheduleForMaterial(selectedMaterial);
    }
  }

  // When fleetTrucks finishes loading, update any existing allocations that have fallback truck details
  $: if (fleetTrucks && fleetTrucks.length > 0 && allocations) {
    let changed = false;
    Object.values(allocations).forEach(sched => {
      if (sched && sched.days) {
        Object.values(sched.days).forEach(dayList => {
          if (Array.isArray(dayList)) {
            dayList.forEach(item => {
              if (item && item.truck && item.truck.id) {
                const match = fleetTrucks.find(t => String(t.id).toUpperCase() === String(item.truck.id).toUpperCase());
                if (match && (!item.truck.type || item.truck.type !== match.type || item.truck.capacity !== match.capacity)) {
                  item.truck = { ...match, ...item.truck };
                  changed = true;
                }
              }
            });
          }
        });
      }
    });
    if (changed) {
      allocations = { ...allocations };
    }
  }

  // Fully reactive derived schedule for currently selected material
  $: currentSchedule = (selectedMaterial && allocations[getMaterialKey(selectedMaterial)])
    ? allocations[getMaterialKey(selectedMaterial)]
    : null;

  // Resolve Load & Disposal addresses and Yard information for currently selected material
  $: hasYard = Boolean(
    selectedMaterial?.yard != null &&
    (
      (typeof selectedMaterial.yard === "object" && (selectedMaterial.yard.id != null || selectedMaterial.yard.name)) ||
      (typeof selectedMaterial.yard === "string" && selectedMaterial.yard.trim() !== "") ||
      typeof selectedMaterial.yard === "number" ||
      selectedMaterial.yard_id != null
    )
  );

  $: resolvedYard = (() => {
    if (!hasYard) return null;
    const rawYard = selectedMaterial.yard || (selectedMaterial.yard_id ? { id: selectedMaterial.yard_id, name: selectedMaterial.yard_name } : null);
    if (!rawYard) return null;

    const yId = typeof rawYard === "object" ? rawYard.id : rawYard;
    const yName = typeof rawYard === "object" ? rawYard.name : null;

    const matched = ($yards || []).find(y => 
      (yId != null && String(y.id) === String(yId)) ||
      (yName && y.name && y.name.trim().toLowerCase() === String(yName).trim().toLowerCase())
    );

    return {
      ...(matched || {}),
      ...(typeof rawYard === "object" ? rawYard : {}),
      contacts: (matched?.contacts && matched.contacts.length > 0) ? matched.contacts : (rawYard?.contacts || []),
      image: (typeof rawYard === "object" && rawYard.image) || matched?.image || null,
      name: (typeof rawYard === "object" && rawYard.name) || matched?.name || (typeof rawYard === "string" ? rawYard : "Disposal Facility"),
      link: (typeof rawYard === "object" && rawYard.link) || matched?.link || selectedMaterial?.yard_link || null
    };
  })();

  let copiedLinkType = null;
  let copiedTimeout = null;

  async function handleCopyLiveLink(url, type = "yard") {
    if (!url) return;
    const ok = await copyToClipboard(url);
    if (ok) {
      copiedLinkType = type;
      if (copiedTimeout) clearTimeout(copiedTimeout);
      copiedTimeout = setTimeout(() => {
        copiedLinkType = null;
      }, 2000);
    }
  }

  $: resolvedYardLiveLink = (() => {
    if (resolvedYard?.link) return resolvedYard.link;
    if (selectedMaterial?.yard_link) return selectedMaterial.yard_link;
    if (typeof selectedMaterial?.yard === "object" && selectedMaterial.yard?.link) return selectedMaterial.yard.link;
    const yId = selectedMaterial?.yard_id ?? (typeof selectedMaterial?.yard === "object" ? selectedMaterial.yard?.id : selectedMaterial?.yard);
    if (yId != null && Array.isArray($yards)) {
      const matched = $yards.find(y => String(y.id) === String(yId));
      if (matched?.link) return matched.link;
    }
    return null;
  })();

  $: resolvedYardLiveUrl = resolvedYardLiveLink ? getYardLiveUrl(resolvedYardLiveLink) : "";

  $: currentJobObj = currentJob || ((Array.isArray(effectiveJobs) && currentJobId) ? effectiveJobs.find(j => isJobCurrent(j)) : null);
  $: clientJobLiveLink = currentJobObj?.link || null;
  $: clientJobLiveUrl = clientJobLiveLink ? getClientJobLiveUrl(clientJobLiveLink) : "";

  $: yardMainContact = (() => {
    if (!resolvedYard) return null;
    const contacts = Array.isArray(resolvedYard.contacts) ? resolvedYard.contacts : [];
    const main = contacts.find(c => c.main === true || c.is_main === true) || contacts[0];
    if (main && (main.name || main.phone || main.email)) {
      return main;
    }
    if (resolvedYard.main_contact && typeof resolvedYard.main_contact === "object") {
      return resolvedYard.main_contact;
    }
    if (resolvedYard.contact && typeof resolvedYard.contact === "object") {
      return resolvedYard.contact;
    }
    if (resolvedYard.contact_name) {
      return {
        name: resolvedYard.contact_name,
        phone: resolvedYard.phone || "",
        email: resolvedYard.email || "",
        position: resolvedYard.position || ""
      };
    }
    return null;
  })();

  $: resolvedLoadAddress = (() => {
    if (!selectedMaterial) return null;
    return (
      selectedMaterial.load_address ||
      selectedMaterial.siteAddress ||
      selectedMaterial.loadAddress ||
      null
    );
  })();

  $: resolvedDisposalAddress = (() => {
    if (!selectedMaterial) return null;
    return (
      selectedMaterial.disposal_address ||
      selectedMaterial.dispose_address ||
      selectedMaterial.yard?.address ||
      resolvedYard?.address ||
      (selectedMaterial.yard?.suburb ? { suburb: selectedMaterial.yard.suburb } : null) ||
      (resolvedYard?.suburb ? { suburb: resolvedYard.suburb } : null) ||
      null
    );
  })();

  let tabLoadAddressWarning = "";
  let tabDisposalAddressWarning = "";

  $: selectedLoadAddressStr = (() => {
    if (!selectedMaterial) return "";
    if (typeof selectedMaterial.load_address === "string") return selectedMaterial.load_address;
    if (selectedMaterial.load_address?.place_name) return selectedMaterial.load_address.place_name;
    if (selectedMaterial.load_address?.street) return inlineAddress(selectedMaterial.load_address);
    if (selectedMaterial.siteAddress) return selectedMaterial.siteAddress;
    if (selectedMaterial.loadAddress) return selectedMaterial.loadAddress;
    return "";
  })();

  $: selectedDisposalAddressStr = (() => {
    if (!selectedMaterial) return "";
    if (typeof selectedMaterial.disposal_address === "string") return selectedMaterial.disposal_address;
    if (selectedMaterial.disposal_address?.place_name) return selectedMaterial.disposal_address.place_name;
    if (selectedMaterial.disposal_address?.street) return inlineAddress(selectedMaterial.disposal_address);
    if (selectedMaterial.dispose_address) return selectedMaterial.dispose_address;
    if (selectedMaterial.yard?.address) {
      return typeof selectedMaterial.yard.address === "string" ? selectedMaterial.yard.address : inlineAddress(selectedMaterial.yard.address);
    }
    if (resolvedYard?.address) {
      return typeof resolvedYard.address === "string" ? resolvedYard.address : inlineAddress(resolvedYard.address);
    }
    if (selectedMaterial.yard?.suburb) {
      return `${selectedMaterial.yard?.name || "Disposal Facility"}, ${selectedMaterial.yard.suburb}`;
    }
    return "";
  })();

  $: tabYardContactName =
    selectedMaterial?.contact_name ||
    selectedMaterial?.main_contact?.name ||
    yardMainContact?.name ||
    "";

  $: tabYardContactPhone =
    selectedMaterial?.contact_phone ||
    selectedMaterial?.main_contact?.phone ||
    yardMainContact?.phone ||
    "";

  $: tabYardContactEmail =
    selectedMaterial?.contact_email ||
    selectedMaterial?.main_contact?.email ||
    yardMainContact?.email ||
    "";

  $: tabYardContactPosition =
    selectedMaterial?.contact_position ||
    selectedMaterial?.main_contact?.position ||
    yardMainContact?.position ||
    "";

  function syncSelectedMaterialToMaterials() {
    if (!selectedMaterial) return;
    if (Array.isArray(materials)) {
      const idx = materials.findIndex(m => getMaterialKey(m) === getMaterialKey(selectedMaterial));
      if (idx !== -1) {
        materials[idx] = { ...materials[idx], ...selectedMaterial };
        materials = [...materials];
      }
    }
    dispatch("material-change", { material: selectedMaterial });
  }

  function handleTabLoadAddressResult(e) {
    const result = e.detail?.result || e.detail?.detail?.result;
    if (!result || !selectedMaterial) return;
    const placeName = result.place_name || "";
    const isAddressFeature = result.place_type?.includes("address");
    const parsed = parseMapboxAddress(result);
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      tabLoadAddressWarning = "";
    } else {
      tabLoadAddressWarning = "Full address required: MUST include street and suburb (e.g. Church St, Parramatta).";
    }

    const loadAddrObj = {
      street: parsed?.street || validation.street || placeName,
      suburb: parsed?.suburb || validation.suburb || "",
      state: parsed?.state || "NSW",
      postcode: parsed?.postcode || "",
      name: parsed?.name || null,
      place_name: placeName,
      coordinates: parsed?.coordinates || null,
      is_full_address: Boolean(isAddressFeature || validation.valid)
    };

    selectedMaterial.load_address = loadAddrObj;
    selectedMaterial.siteAddress = placeName;
    selectedMaterial.loadAddress = placeName;
    if (loadAddrObj.coordinates) {
      selectedMaterial.load_coordinates = loadAddrObj.coordinates;
      if (!selectedMaterial.load) selectedMaterial.load = {};
      selectedMaterial.load.coordinates = loadAddrObj.coordinates;
    }
    syncSelectedMaterialToMaterials();
  }

  function handleTabLoadAddressClear() {
    if (!selectedMaterial) return;
    tabLoadAddressWarning = "";
    selectedMaterial.load_address = null;
    selectedMaterial.siteAddress = "";
    selectedMaterial.loadAddress = "";
    selectedMaterial.load_coordinates = null;
    if (selectedMaterial.load) selectedMaterial.load.coordinates = null;
    syncSelectedMaterialToMaterials();
  }

  function handleTabDisposalAddressResult(e) {
    const result = e.detail?.result || e.detail?.detail?.result;
    if (!result || !selectedMaterial) return;
    const placeName = result.place_name || "";
    const isAddressFeature = result.place_type?.includes("address");
    const parsed = parseMapboxAddress(result);
    const validation = validateFullAddress(placeName);

    if (isAddressFeature || validation.valid) {
      tabDisposalAddressWarning = "";
    } else {
      tabDisposalAddressWarning = "Full address required: MUST include street and suburb.";
    }

    const dispAddrObj = {
      street: parsed?.street || validation.street || placeName,
      suburb: parsed?.suburb || validation.suburb || "",
      state: parsed?.state || "NSW",
      postcode: parsed?.postcode || "",
      name: parsed?.name || null,
      place_name: placeName,
      coordinates: parsed?.coordinates || null,
      is_full_address: Boolean(isAddressFeature || validation.valid)
    };

    selectedMaterial.disposal_address = dispAddrObj;
    selectedMaterial.dispose_address = placeName;
    if (dispAddrObj.suburb) {
      selectedMaterial.suburb = dispAddrObj.suburb;
    }
    if (dispAddrObj.coordinates) {
      selectedMaterial.disposal_coordinates = dispAddrObj.coordinates;
    }
    syncSelectedMaterialToMaterials();
  }

  function handleTabDisposalAddressClear() {
    if (!selectedMaterial) return;
    tabDisposalAddressWarning = "";
    selectedMaterial.disposal_address = null;
    selectedMaterial.dispose_address = "";
    selectedMaterial.disposal_coordinates = null;
    syncSelectedMaterialToMaterials();
  }

  function handleTabYardSelectChange(e) {
    const selectedYardId = e.target.value;
    if (!selectedMaterial) return;
    if (!selectedYardId || selectedYardId === "custom") {
      if (selectedYardId === "custom") {
        selectedMaterial.yard_id = null;
        selectedMaterial.image = null;
      }
      syncSelectedMaterialToMaterials();
      return;
    }
    const matchedYard = ($yards || []).find((y) => String(y.id) === String(selectedYardId));
    if (matchedYard) {
      selectedMaterial.yard_id = matchedYard.id;
      selectedMaterial.yard_name = matchedYard.name;
      selectedMaterial.yard = matchedYard;
      selectedMaterial.image = matchedYard.image || null;
      selectedMaterial.suburb = matchedYard.address?.suburb || matchedYard.suburb || "";

      if (matchedYard.address) {
        const addrStr = typeof matchedYard.address === "string" ? matchedYard.address : inlineAddress(matchedYard.address);
        selectedMaterial.disposal_address = {
          street: matchedYard.address.street || matchedYard.name,
          suburb: matchedYard.address.suburb || matchedYard.suburb || "",
          state: matchedYard.address.state || "NSW",
          postcode: matchedYard.address.postcode || "",
          place_name: addrStr,
          coordinates: matchedYard.address.coordinates || null,
          is_full_address: true
        };
        selectedMaterial.dispose_address = addrStr;
      }

      const contacts = Array.isArray(matchedYard.contacts) ? matchedYard.contacts : [];
      const main = contacts.find((c) => c.main === true || c.is_main === true) || contacts[0];
      if (main) {
        selectedMaterial.contact_name = main.name || "";
        selectedMaterial.contact_phone = main.phone || "";
        selectedMaterial.contact_email = main.email || "";
        selectedMaterial.contact_position = main.position || "";
        selectedMaterial.main_contact = { ...main };
      } else if (matchedYard.phone) {
        selectedMaterial.contact_name = matchedYard.contact_name || "Weighbridge / Dispatch";
        selectedMaterial.contact_phone = matchedYard.phone || "";
        selectedMaterial.contact_email = matchedYard.email || "";
        selectedMaterial.contact_position = "Dispatch";
        selectedMaterial.main_contact = {
          name: selectedMaterial.contact_name,
          phone: selectedMaterial.contact_phone,
          email: selectedMaterial.contact_email,
          position: selectedMaterial.contact_position
        };
      }

      syncSelectedMaterialToMaterials();
    }
  }

  function handleTabYardNameInput(val) {
    if (!selectedMaterial) return;
    selectedMaterial.yard_name = val;
    if (typeof selectedMaterial.yard === "object" && selectedMaterial.yard !== null) {
      selectedMaterial.yard.name = val;
    }
    syncSelectedMaterialToMaterials();
  }

  function handleTabYardSuburbInput(val) {
    if (!selectedMaterial) return;
    selectedMaterial.suburb = val;
    if (typeof selectedMaterial.yard === "object" && selectedMaterial.yard !== null) {
      selectedMaterial.yard.suburb = val;
    }
    syncSelectedMaterialToMaterials();
  }

  function updateTabContactField(fieldName, value) {
    if (!selectedMaterial) return;
    if (!selectedMaterial.main_contact) selectedMaterial.main_contact = {};
    if (fieldName === "name") {
      selectedMaterial.contact_name = value;
      selectedMaterial.main_contact.name = value;
    } else if (fieldName === "phone") {
      selectedMaterial.contact_phone = value;
      selectedMaterial.main_contact.phone = value;
    } else if (fieldName === "email") {
      selectedMaterial.contact_email = value;
      selectedMaterial.main_contact.email = value;
    } else if (fieldName === "position") {
      selectedMaterial.contact_position = value;
      selectedMaterial.main_contact.position = value;
    }
    syncSelectedMaterialToMaterials();
  }

  function getAddressStreet(addr, fallback = "Address not specified") {
    if (!addr) return fallback;
    if (typeof addr === "string") {
      const val = validateFullAddress(addr);
      if (val.valid && val.street) {
        return val.street;
      }
      const parts = addr.split(",").map(p => p.trim()).filter(Boolean);
      return parts[0] || addr;
    }
    return addr.street || addr.name || addr.address || fallback;
  }

  function getAddressLocality(addr) {
    if (!addr) return "";
    if (typeof addr === "string") {
      const val = validateFullAddress(addr);
      if (val.valid && val.suburb) {
        const parts = addr.split(",").map(p => p.trim()).filter(Boolean);
        return parts.slice(1).join(", ") || val.suburb;
      }
      const parts = addr.split(",").map(p => p.trim()).filter(Boolean);
      return parts.slice(1).join(", ");
    }
    const parts = [];
    if (addr.suburb && addr.suburb !== addr.street && addr.suburb !== addr.name) {
      parts.push(addr.suburb);
    }
    if (addr.state) parts.push(addr.state);
    if (addr.postcode) parts.push(addr.postcode);
    return parts.join(" ");
  }

  function getMaterialKey(mat) {
    if (!mat) return "mat_0";
    return String(mat.id || mat.__job_material_number__ || mat.material_name || mat.name || "mat_default");
  }

  function computeEstimatedDays(mat) {
    if (!mat) return 1;
    if (mat.schedule?.estimated_days) {
      return Math.max(1, Math.ceil(mat.schedule.estimated_days));
    }
    if (mat.schedule?.full_days) {
      return Math.max(1, Math.ceil(mat.schedule.full_days));
    }
    const tonnes = Number(mat.total_tonnes || mat.quantity || 0);
    const capacity = Number(mat.fleet?.total_capacity_tonne || 38);
    const runs = Number(mat.schedule?.runs_per_day || 4);
    const trucks = Number(mat.schedule?.allocated_trucks || mat.fleet?.allocated_trucks || 2);
    const dailyCap = capacity * runs * trucks;
    if (dailyCap > 0 && tonnes > 0) {
      return Math.max(1, Math.ceil(tonnes / dailyCap));
    }
    return 2; // Default reasonable haulage project duration
  }

  function getCurrentSchedule() {
    if (!selectedMaterial) return null;
    const key = getMaterialKey(selectedMaterial);
    if (!allocations[key]) {
      initScheduleForMaterial(selectedMaterial);
    }
    return allocations[key] || null;
  }

  function setDaysCount(delta) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current) return;
    const newCount = Math.max(1, Math.min(60, current.daysCount + delta));
    current.daysCount = newCount;
    for (let i = 0; i < newCount; i++) {
      if (!current.days[i]) {
        current.days[i] = [];
      }
    }
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function setDaysCountExplicit(newDays) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current) return;
    const newCount = Math.max(1, Math.min(60, parseInt(newDays, 10) || 1));
    current.daysCount = newCount;
    for (let i = 0; i < newCount; i++) {
      if (!current.days[i]) {
        current.days[i] = [];
      }
    }
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function clearConflictAlert() {
    if (conflictAlertTimeout) {
      clearTimeout(conflictAlertTimeout);
      conflictAlertTimeout = null;
    }
    allocationConflictAlert = null;
  }

  function showConflictAlert(conflict) {
    if (!conflict) return;
    allocationConflictAlert = conflict;
    if (conflictAlertTimeout) clearTimeout(conflictAlertTimeout);
    conflictAlertTimeout = setTimeout(() => {
      allocationConflictAlert = null;
      conflictAlertTimeout = null;
    }, 15000);
  }

  function handleOpenConflictingJob(conflict) {
    if (!conflict || !conflict.job) return;
    clearConflictAlert();
    isFleetDrawerOpen = false;
    dispatch("select-job", conflict.job);
  }

  function normalizeDateToISO(val) {
    if (!val) return null;
    const str = String(val).trim();
    const match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
      const yyyy = match[1];
      const mm = match[2].padStart(2, "0");
      const dd = match[3].padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    const auMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (auMatch) {
      const dd = auMatch[1].padStart(2, "0");
      const mm = auMatch[2].padStart(2, "0");
      const yyyy = auMatch[3];
      return `${yyyy}-${mm}-${dd}`;
    }
    const parsed = new Date(val);
    if (!isNaN(parsed.getTime())) {
      const yyyy = parsed.getFullYear();
      const mm = String(parsed.getMonth() + 1).padStart(2, "0");
      const dd = String(parsed.getDate()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}`;
    }
    return null;
  }

  function formatDateAU(isoDateStr) {
    if (!isoDateStr) return "";
    const iso = normalizeDateToISO(isoDateStr);
    if (!iso) return String(isoDateStr);
    const parts = iso.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return isoDateStr;
  }

  function isJobCurrent(j) {
    if (!j || !currentJobId) return false;
    const target = String(currentJobId).trim();
    if (!target) return false;
    const jId = j.id != null ? String(j.id).trim() : "";
    const jNum = j.__job_number__ != null ? String(j.__job_number__).trim() : "";
    const jMongo = j._id != null ? String(j._id).trim() : "";
    const jQId = j.quote_id != null ? String(j.quote_id).trim() : "";
    const jQNum = j.quote_number != null ? String(j.quote_number).trim() : "";
    return (
      jId === target ||
      jNum === target ||
      jMongo === target ||
      jQId === target ||
      jQNum === target
    );
  }

  function getTruckConflict(truckId, dayIndex) {
    if (!truckId) return null;
    const targetTruckId = String(truckId).trim().toUpperCase();
    if (!targetTruckId) return null;

    const currentBaseDate = selectedMaterial?.start_date || startDate || new Date().toISOString().split("T")[0];
    const targetDateISO = normalizeDateToISO(calculateScheduledDate(currentBaseDate, (Number(dayIndex) || 0) + 1));
    if (!targetDateISO) return null;

    const dateFormattedAU = formatDateAU(targetDateISO);

    // 1. Check external jobs
    const jobsList = effectiveJobs || [];
    for (const j of jobsList) {
      if (!j) continue;
      // Skip current job being edited
      if (isJobCurrent(j)) continue;
      // Skip cancelled jobs
      const status = String(j.status || "").trim().toLowerCase();
      if (status === "cancelled") continue;

      const jobNumber = j.__job_number__ || j.id || j._id || "Unknown";
      const customerName = getJobCustomerName(j);

      // Check materials in job
      const mats = Array.isArray(j.materials) ? j.materials : [];
      for (const m of mats) {
        const allocs = Array.isArray(m.allocated_trucks) ? m.allocated_trucks : [];
        for (const alloc of allocs) {
          const allocTruckId = String(
            alloc.truck?.id || alloc.truck?.rego || alloc.truck_id || alloc.rego || alloc.truck || ""
          ).trim().toUpperCase();

          if (allocTruckId === targetTruckId) {
            let allocDateISO = normalizeDateToISO(alloc.scheduled_date);
            if (!allocDateISO) {
              const baseDate = m.start_date || j.start_date || j.quote_date || j.created_at;
              allocDateISO = normalizeDateToISO(calculateScheduledDate(baseDate, alloc.scheduled_day || 1));
            }

            if (allocDateISO && allocDateISO === targetDateISO) {
              const matName = m.name || m.material_name || "Material";
              return {
                type: "job",
                truckId: targetTruckId,
                date: targetDateISO,
                dateFormatted: dateFormattedAU,
                dayNumber: (Number(dayIndex) || 0) + 1,
                jobId: jobNumber,
                job: j,
                jobTitle: `Job #${jobNumber} (${customerName})`,
                customerName,
                materialName: matName,
                reason: `Truck ${targetTruckId} is already allocated to Job #${jobNumber} (${customerName} - ${matName}) on ${dateFormattedAU}.`
              };
            }
          }
        }
      }

      // Check root-level allocated_trucks if any
      const rootAllocs = Array.isArray(j.allocated_trucks) ? j.allocated_trucks : [];
      for (const alloc of rootAllocs) {
        const allocTruckId = String(
          alloc.truck?.id || alloc.truck?.rego || alloc.truck_id || alloc.rego || alloc.truck || ""
        ).trim().toUpperCase();

        if (allocTruckId === targetTruckId) {
          let allocDateISO = normalizeDateToISO(alloc.scheduled_date);
          if (!allocDateISO) {
            allocDateISO = normalizeDateToISO(calculateScheduledDate(j.start_date || j.quote_date, alloc.scheduled_day || 1));
          }

          if (allocDateISO && allocDateISO === targetDateISO) {
            return {
              type: "job",
              truckId: targetTruckId,
              date: targetDateISO,
              dateFormatted: dateFormattedAU,
              dayNumber: (Number(dayIndex) || 0) + 1,
              jobId: jobNumber,
              job: j,
              jobTitle: `Job #${jobNumber} (${customerName})`,
              customerName,
              materialName: "General Haulage",
              reason: `Truck ${targetTruckId} is already allocated to Job #${jobNumber} (${customerName}) on ${dateFormattedAU}.`
            };
          }
        }
      }
    }

    // 2. Check other materials in the current allocations store for this same day
    if (allocations && typeof allocations === "object" && Array.isArray(materials) && materials.length > 1) {
      const currentMatKey = selectedMaterial ? String(getMaterialKey(selectedMaterial)) : null;
      const currentMatName = (selectedMaterial?.material_name || selectedMaterial?.name || "").trim().toLowerCase();
      const activeMatKeys = new Set(materials.map(m => String(getMaterialKey(m))));

      for (const [matKey, allocEntry] of Object.entries(allocations)) {
        if (!allocEntry || String(matKey) === currentMatKey) continue;
        if (!activeMatKeys.has(String(matKey))) continue;
        const entryMatName = (allocEntry.materialName || "").trim().toLowerCase();
        if (currentMatName && entryMatName === currentMatName && materials.filter(m => (m.material_name || m.name || "").trim().toLowerCase() === currentMatName).length <= 1) {
          continue;
        }
        const trucksInDay = allocEntry.days?.[dayIndex] || [];
        const isConflict = trucksInDay.some(item => {
          const tid = String(item.truck?.id || item.truck?.rego || item.truck || "").trim().toUpperCase();
          return tid === targetTruckId;
        });

        if (isConflict) {
          const matLabel = allocEntry.materialName || matKey;
          return {
            type: "material",
            truckId: targetTruckId,
            date: targetDateISO,
            dateFormatted: dateFormattedAU,
            dayNumber: (Number(dayIndex) || 0) + 1,
            jobId: matLabel,
            materialName: matLabel,
            jobTitle: `Current Job (${matLabel})`,
            reason: `Truck ${targetTruckId} is already allocated to Material '${matLabel}' on ${dateFormattedAU} in this Job.`
          };
        }
      }
    }

    return null;
  }

  function isTruckInDay(sched, truckId, dayIndex) {
    if (!sched || !sched.days) return false;
    const dayList = sched.days[dayIndex];
    if (!Array.isArray(dayList)) return false;
    return dayList.some(item => String(item.truck?.id) === String(truckId));
  }

  function computeDayDate(baseDateStr, dayOffset) {
    try {
      const parts = (baseDateStr || "").split("-");
      if (parts.length === 3) {
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        d.setDate(d.getDate() + dayOffset);
        return d.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" });
      }
    } catch (_) {}
    return `Day ${dayOffset + 1}`;
  }

  function calculateStartTime(baseTimeStr, truckIndex, loadMins) {
    if (!baseTimeStr) baseTimeStr = "07:00";
    const [hStr, mStr] = baseTimeStr.split(":");
    let hours = parseInt(hStr, 10) || 7;
    let minutes = parseInt(mStr, 10) || 0;

    const totalMinutes = hours * 60 + minutes + (truckIndex * loadMins);
    const scheduledHours = Math.floor(totalMinutes / 60) % 24;
    const scheduledMinutes = totalMinutes % 60;

    const hh = String(scheduledHours).padStart(2, "0");
    const mm = String(scheduledMinutes).padStart(2, "0");
    return `${hh}:${mm}`;
  }

  function formatDisplayTime(timeStr) {
    if (!timeStr) return "07:00 AM";
    const [hStr, mStr] = timeStr.split(":");
    let hours = parseInt(hStr, 10);
    const minutes = mStr || "00";
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${ampm}`;
  }

  function recalculateDayTimes(dayList, startTime, loadInterval) {
    if (!Array.isArray(dayList)) return [];
    return dayList.map((item, idx) => ({
      ...item,
      scheduledStartTime: calculateStartTime(startTime, idx, loadInterval)
    }));
  }

  // Drag and Drop
  function handleDragStart(event, truck) {
    draggedTruck = truck;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "copyMove";
      event.dataTransfer.setData("text/plain", String(truck.id));
      try {
        event.dataTransfer.setData("application/json", JSON.stringify(truck));
      } catch (_) {}

      const cardEl = event.currentTarget ? event.currentTarget.closest('.draggable-truck-card') : null;
      if (cardEl && event.dataTransfer.setDragImage) {
        event.dataTransfer.setDragImage(cardEl, 15, 25);
      }
    }
  }

  function handleDragOver(event, dayIndex) {
    event.preventDefault();
    let truck = draggedTruck || resolveTruckFromEvent(event);
    if (!truck && draggedAllocatedTruck) {
      const key = getMaterialKey(selectedMaterial);
      truck = allocations[key]?.days?.[draggedAllocatedTruck.dayIndex]?.[draggedAllocatedTruck.truckIndex]?.truck;
    }
    const conflict = truck ? getTruckConflict(truck.id, dayIndex) : null;
    if (conflict) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "none";
      }
      dragOverDayIndex = dayIndex;
      dragOverConflict = true;
    } else {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = draggedAllocatedTruck ? "move" : "copy";
      }
      dragOverDayIndex = dayIndex;
      dragOverConflict = false;
    }
  }

  function handleDragLeave(event, dayIndex) {
    if (event.currentTarget && !event.currentTarget.contains(event.relatedTarget)) {
      if (dragOverDayIndex === dayIndex) {
        dragOverDayIndex = null;
        dragOverConflict = false;
      }
    }
  }

  function handleDragEnd() {
    draggedTruck = null;
    draggedAllocatedTruck = null;
    dragOverDayIndex = null;
    dragOverAllocatedTruck = null;
    dragOverConflict = false;
  }

  function resolveTruckFromEvent(event) {
    let truckObj = draggedTruck;
    if (!truckObj && event && event.dataTransfer) {
      try {
        const raw = event.dataTransfer.getData("application/json");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && parsed.id && parsed.source !== "day-reorder") {
            truckObj = parsed;
          }
        }
      } catch (_) {}

      if (!truckObj) {
        try {
          const truckId = event.dataTransfer.getData("text/plain");
          if (truckId && fleetTrucks) {
            truckObj = fleetTrucks.find(t => String(t.id).toUpperCase() === String(truckId).toUpperCase());
          }
        } catch (_) {}
      }
    }

    if (truckObj && fleetTrucks && fleetTrucks.length > 0) {
      const match = fleetTrucks.find(t => String(t.id).toUpperCase() === String(truckObj.id).toUpperCase());
      if (match) {
        truckObj = { ...match, ...truckObj };
      }
    }

    return truckObj;
  }

  let draggedAllocatedTruck = null; // { dayIndex, truckIndex, item }
  let dragOverAllocatedTruck = null; // { dayIndex, truckIndex }

  function handleAllocatedDragStart(event, dayIndex, truckIndex, item) {
    draggedAllocatedTruck = { dayIndex, truckIndex, item };
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(item.truck?.id || ""));
      try {
        event.dataTransfer.setData("application/json", JSON.stringify({
          source: "day-reorder",
          dayIndex,
          truckIndex
        }));
      } catch (_) {}

      const cardEl = event.currentTarget ? event.currentTarget.closest('.scheduled-truck-card') : null;
      if (cardEl && event.dataTransfer.setDragImage) {
        event.dataTransfer.setDragImage(cardEl, 15, 25);
      }
    }
  }

  function handleAllocatedDragOver(event, dayIndex, truckIndex) {
    event.preventDefault();
    let truck = draggedTruck || resolveTruckFromEvent(event);
    if (!truck && draggedAllocatedTruck) {
      const key = getMaterialKey(selectedMaterial);
      truck = allocations[key]?.days?.[draggedAllocatedTruck.dayIndex]?.[draggedAllocatedTruck.truckIndex]?.truck;
    }
    const conflict = truck ? getTruckConflict(truck.id, dayIndex) : null;
    if (conflict) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "none";
      }
      dragOverDayIndex = dayIndex;
      dragOverConflict = true;
      dragOverAllocatedTruck = { dayIndex, truckIndex };
    } else {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = draggedAllocatedTruck ? "move" : "copy";
      }
      dragOverDayIndex = dayIndex;
      dragOverConflict = false;
      dragOverAllocatedTruck = { dayIndex, truckIndex };
    }
  }

  function handleAllocatedDragLeave(event, dayIndex, truckIndex) {
    if (event.currentTarget && !event.currentTarget.contains(event.relatedTarget)) {
      if (dragOverAllocatedTruck && dragOverAllocatedTruck.dayIndex === dayIndex && dragOverAllocatedTruck.truckIndex === truckIndex) {
        dragOverAllocatedTruck = null;
      }
    }
  }

  function handleAllocatedDrop(event, targetDayIndex, targetTruckIndex) {
    event.preventDefault();
    event.stopPropagation();
    dragOverDayIndex = null;
    dragOverAllocatedTruck = null;
    dragOverConflict = false;

    if (!selectedMaterial) return;

    if (draggedAllocatedTruck) {
      const key = getMaterialKey(selectedMaterial);
      const current = allocations[key];
      if (!current || !current.days) return;

      const srcDay = draggedAllocatedTruck.dayIndex;
      const srcIdx = draggedAllocatedTruck.truckIndex;
      draggedAllocatedTruck = null;

      const baseStartTime = current.startTime || "07:00";
      const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

      if (srcDay === targetDayIndex) {
        if (srcIdx === targetTruckIndex) return;
        const list = [...(current.days[srcDay] || [])];
        const [movedItem] = list.splice(srcIdx, 1);
        list.splice(targetTruckIndex, 0, movedItem);
        current.days[srcDay] = recalculateDayTimes(list, baseStartTime, loadInterval);
      } else {
        const movedItem = (current.days[srcDay] || [])[srcIdx];
        const conflict = movedItem ? getTruckConflict(movedItem.truck?.id, targetDayIndex) : null;
        if (conflict) {
          showConflictAlert(conflict);
          return;
        }
        const srcList = [...(current.days[srcDay] || [])];
        const targetList = [...(current.days[targetDayIndex] || [])];
        const [popped] = srcList.splice(srcIdx, 1);
        targetList.splice(targetTruckIndex, 0, popped);
        current.days[srcDay] = recalculateDayTimes(srcList, baseStartTime, loadInterval);
        current.days[targetDayIndex] = recalculateDayTimes(targetList, baseStartTime, loadInterval);
      }

      allocations[key] = { ...current, days: { ...current.days } };
      allocations = { ...allocations };
      emitChanges();
      return;
    }

    // Drop from fleet drawer onto a specific position in the Day column
    const truckToAdd = resolveTruckFromEvent(event);
    if (truckToAdd) {
      const conflict = getTruckConflict(truckToAdd.id, targetDayIndex);
      if (conflict) {
        showConflictAlert(conflict);
        draggedTruck = null;
        return;
      }
      allocateTruckToDay(truckToAdd, targetDayIndex, targetTruckIndex);
      draggedTruck = null;
    }
  }

  function handleDrop(event, dayIndex) {
    event.preventDefault();
    dragOverDayIndex = null;
    dragOverAllocatedTruck = null;
    dragOverConflict = false;

    if (draggedAllocatedTruck) {
      const srcDay = draggedAllocatedTruck.dayIndex;
      const srcIdx = draggedAllocatedTruck.truckIndex;
      draggedAllocatedTruck = null;

      if (!selectedMaterial) return;
      const key = getMaterialKey(selectedMaterial);
      const current = allocations[key];
      if (!current || !current.days) return;

      const baseStartTime = current.startTime || "07:00";
      const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

      if (srcDay === dayIndex) {
        const list = [...(current.days[srcDay] || [])];
        const [movedItem] = list.splice(srcIdx, 1);
        list.push(movedItem);
        current.days[srcDay] = recalculateDayTimes(list, baseStartTime, loadInterval);
      } else {
        const movedItem = (current.days[srcDay] || [])[srcIdx];
        const conflict = movedItem ? getTruckConflict(movedItem.truck?.id, dayIndex) : null;
        if (conflict) {
          showConflictAlert(conflict);
          return;
        }
        const srcList = [...(current.days[srcDay] || [])];
        const targetList = [...(current.days[dayIndex] || [])];
        const [popped] = srcList.splice(srcIdx, 1);
        targetList.push(popped);
        current.days[srcDay] = recalculateDayTimes(srcList, baseStartTime, loadInterval);
        current.days[dayIndex] = recalculateDayTimes(targetList, baseStartTime, loadInterval);
      }

      allocations[key] = { ...current, days: { ...current.days } };
      allocations = { ...allocations };
      emitChanges();
      return;
    }

    const truckToAdd = resolveTruckFromEvent(event);
    if (!truckToAdd || !selectedMaterial) return;

    const conflict = getTruckConflict(truckToAdd.id, dayIndex);
    if (conflict) {
      showConflictAlert(conflict);
      draggedTruck = null;
      return;
    }

    allocateTruckToDay(truckToAdd, dayIndex, -1);
    draggedTruck = null;
  }

  function allocateTruckToDay(truck, dayIndex, insertIndex = -1) {
    if (!truck || !selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    if (!allocations[key]) {
      initScheduleForMaterial(selectedMaterial);
    }
    const current = allocations[key];
    if (!current) return;

    const dayIdx = Number(dayIndex) || 0;
    const conflict = getTruckConflict(truck.id, dayIdx);
    if (conflict) {
      showConflictAlert(conflict);
      return;
    }

    if (!current.days) current.days = {};
    if (!current.days[dayIdx]) current.days[dayIdx] = [];

    const dayList = current.days[dayIdx];

    // Prevent duplicate of the same truck in the same day (case-insensitive)
    const alreadyInDay = dayList.some(item => String(item.truck?.id || "").toUpperCase() === String(truck.id || "").toUpperCase());
    if (alreadyInDay) return;

    const trl = truck.default_trailer || truck.trailer;
    const newItem = {
      truck: { ...truck },
      trailer: trl ? { ...trl } : null,
      driver: truck.default_driver ? { ...truck.default_driver } : { name: `Driver ${truck.id}`, phone: "" },
      scheduledStartTime: ""
    };

    const updatedList = [...dayList];
    if (insertIndex >= 0 && insertIndex <= updatedList.length) {
      updatedList.splice(insertIndex, 0, newItem);
    } else {
      updatedList.push(newItem);
    }

    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    current.days[dayIdx] = recalculateDayTimes(updatedList, baseStartTime, loadInterval);
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function removeTruckFromDay(dayIndex, truckIndex) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days || !current.days[dayIndex]) return;

    const list = [...current.days[dayIndex]];
    list.splice(truckIndex, 1);
    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    current.days[dayIndex] = recalculateDayTimes(list, baseStartTime, loadInterval);
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function removeTruckFromDayByTruckId(truckId, dayIndex) {
    if (!selectedMaterial || !truckId) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days || !current.days[dayIndex]) return;

    const list = current.days[dayIndex];
    const idx = list.findIndex(item => String(item.truck?.id || item.truck) === String(truckId));
    if (idx !== -1) {
      removeTruckFromDay(dayIndex, idx);
    }
  }

  function toggleTruckDayAllocation(truck, dayIndex) {
    if (!truck || !selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (isTruckInDay(current, truck.id, dayIndex)) {
      removeTruckFromDayByTruckId(truck.id, dayIndex);
    } else {
      allocateTruckToDay(truck, dayIndex);
    }
  }

  function moveTruckBetweenDays(fromDay, truckIndex, toDay) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days || !current.days[fromDay]) return;

    const item = current.days[fromDay][truckIndex];
    if (item) {
      const conflict = getTruckConflict(item.truck?.id, toDay);
      if (conflict) {
        showConflictAlert(conflict);
        return;
      }
    }

    const [moved] = current.days[fromDay].splice(truckIndex, 1);
    if (!moved) return;

    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    current.days[fromDay] = recalculateDayTimes(current.days[fromDay], baseStartTime, loadInterval);
    if (!current.days[toDay]) current.days[toDay] = [];
    current.days[toDay].push(moved);
    current.days[toDay] = recalculateDayTimes(current.days[toDay], baseStartTime, loadInterval);

    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function moveTruckWithinDay(dayIndex, currentIndex, direction) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days || !current.days[dayIndex]) return;

    const list = [...current.days[dayIndex]];
    const targetIndex = currentIndex + direction;
    if (targetIndex < 0 || targetIndex >= list.length) return;

    const temp = list[currentIndex];
    list[currentIndex] = list[targetIndex];
    list[targetIndex] = temp;

    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    current.days[dayIndex] = recalculateDayTimes(list, baseStartTime, loadInterval);
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function clearDaySchedule(dayIndex) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days) return;

    current.days[dayIndex] = [];
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function copyDayToNextDay(fromDayIndex) {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days || !current.days[fromDayIndex]) return;

    const sourceTrucks = current.days[fromDayIndex];
    if (!sourceTrucks || sourceTrucks.length === 0) return;

    const toDayIndex = fromDayIndex + 1;
    if (toDayIndex >= 60) return;

    // Automatically expand haulage duration if copying past current daysCount
    if (toDayIndex >= current.daysCount) {
      current.daysCount = toDayIndex + 1;
    }

    if (!current.days[toDayIndex]) {
      current.days[toDayIndex] = [];
    }

    const targetList = [...current.days[toDayIndex]];
    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    let conflictFound = null;

    for (const item of sourceTrucks) {
      const conflict = getTruckConflict(item.truck?.id, toDayIndex);
      if (conflict) {
        conflictFound = conflict;
        continue;
      }

      const alreadyPresent = targetList.some(
        t => String(t.truck?.id || "").toUpperCase() === String(item.truck?.id || "").toUpperCase()
      );
      if (!alreadyPresent) {
        targetList.push({
          truck: { ...item.truck },
          trailer: item.trailer ? { ...item.trailer } : ((item.truck?.default_trailer || item.truck?.trailer) ? { ...(item.truck.default_trailer || item.truck.trailer) } : null),
          driver: item.driver ? { ...item.driver } : (item.truck?.default_driver || { name: `Driver ${item.truck?.id}`, phone: "" }),
          scheduledStartTime: ""
        });
      }
    }

    if (conflictFound) {
      showConflictAlert(conflictFound);
    }

    current.days[toDayIndex] = recalculateDayTimes(targetList, baseStartTime, loadInterval);
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function updateTiming() {
    if (!selectedMaterial) return;
    const key = getMaterialKey(selectedMaterial);
    const current = allocations[key];
    if (!current || !current.days) return;

    const baseStartTime = current.startTime || "07:00";
    const loadInterval = current.loadInterval != null ? current.loadInterval : 15;

    for (let d = 0; d < current.daysCount; d++) {
      if (current.days[d]) {
        current.days[d] = recalculateDayTimes(current.days[d], baseStartTime, loadInterval);
      }
    }
    allocations[key] = { ...current, days: { ...current.days } };
    allocations = { ...allocations };
    emitChanges();
  }

  function emitChanges() {
    if (selectedMaterial) {
      const key = getMaterialKey(selectedMaterial);
      const current = allocations[key];
      if (current && current.days) {
        const flattened = [];
        Object.entries(current.days).forEach(([dIdx, list]) => {
          if (Array.isArray(list)) {
            list.forEach((item, tOrder) => {
              flattened.push({
                truck: item.truck,
                trailer: item.trailer || item.truck?.default_trailer || item.truck?.trailer || null,
                driver: item.driver || item.truck?.default_driver || null,
                scheduled_start_time: item.scheduledStartTime,
                scheduled_day: parseInt(dIdx, 10) + 1,
                dispatch_order: tOrder + 1,
                id: `${item.truck?.id || 'TRK'}-${parseInt(dIdx, 10) + 1}`
              });
            });
          }
        });
        selectedMaterial.allocated_trucks = flattened;
        selectedMaterial.days_count = current.daysCount;
        if (!selectedMaterial.schedule) selectedMaterial.schedule = {};
        selectedMaterial.schedule.days_count = current.daysCount;
        lastHydratedSignature[key] = computeAllocationSignature(key, flattened);
      }
    }

    dispatch("allocations-changed", {
      allocations,
      selectedMaterialKey: getMaterialKey(selectedMaterial)
    });
  }

  // Filter trucks in fleet drawer
  $: filteredFleet = fleetTrucks.filter(t => {
    if (truckTypeFilter !== "all" && t.type !== truckTypeFilter) return false;
    if (!searchTruckQuery.trim()) return true;
    const q = searchTruckQuery.toLowerCase().trim();
    const id = String(t.id || "").toLowerCase();
    const driver = String(t.default_driver?.name || "").toLowerCase();
    const type = String(t.type || "").toLowerCase();
    const trl = t.default_trailer || t.trailer;
    const trlId = String(trl?.id || "").toLowerCase();
    const trlType = String(trl?.type || "").toLowerCase();
    return id.includes(q) || driver.includes(q) || type.includes(q) || trlId.includes(q) || trlType.includes(q);
  });

  function getDayTotalCapacity(dayList) {
    if (!Array.isArray(dayList)) return 0;
    return dayList.reduce((acc, item) => {
      const truckCap = Number(item.truck?.capacity || 17);
      const trlCap = Number(item.trailer?.capacity || item.truck?.default_trailer?.capacity || item.truck?.trailer?.capacity || 0);
      return acc + (item.truck?.total_capacity || (truckCap + trlCap));
    }, 0);
  }

  function isTruckScheduled(sched, truckId) {
    if (!sched || !sched.days) return false;
    for (let d = 0; d < sched.daysCount; d++) {
      if (sched.days[d]?.some(item => String(item.truck?.id || "").toUpperCase() === String(truckId || "").toUpperCase())) {
        return true;
      }
    }
    return false;
  }
</script>

<svelte:window on:dragend={handleDragEnd} />

<div class="truck-allocation-board">
  <!-- Top Section: Left (Header, Material Switcher, Schedule Params) & Right (Turnaround Schedule & Fleet Capacities Panel) -->
  <div class="allocation-top-layout">
    <div class="allocation-top-left">
      <!-- Section Header -->
      <div class="allocation-board-header">
        <div class="header-main-meta">
          <div class="badge-title-group">
            <span class="board-badge">
              <Location size={14} />
              <span>Location &amp; Disposal</span>
            </span>
            <h3 class="board-title">Site Details</h3>
          </div>
          <p class="board-subtitle">
            Specify loading site location, disposal facility, and contact details for this material.
          </p>
        </div>
      </div>

      <!-- Material Dispatch Locations & Disposal Yard Panel -->
      {#if selectedMaterial}
        <div class="material-dispatch-info-card">
          {#if clientJobLiveUrl}
            <div class="client-live-banner">
              <span class="live-link-tag client-tag">
                <span class="live-pulse-dot"></span>
                <span>CLIENT LIVE TRACKING:</span>
              </span>
              <a
                href={clientJobLiveUrl}
                target="_blank"
                rel="noopener noreferrer"
                class="live-link-anchor"
                title="Open client live tracking map in new tab"
              >
                <span class="live-url-text">{clientJobLiveUrl}</span>
                <Launch size={11} class="launch-icon" />
              </a>
              <button
                type="button"
                class="live-copy-btn {copiedLinkType === 'client' ? 'copied' : ''}"
                title="Copy client tracking URL"
                on:click={() => handleCopyLiveLink(clientJobLiveUrl, 'client')}
              >
                {#if copiedLinkType === 'client'}
                  <Checkmark size={11} />
                  <span>Copied!</span>
                {:else}
                  <Copy size={11} />
                  <span>Copy</span>
                {/if}
              </button>
            </div>
          {/if}

          <!-- Route Locations Row (Load Address & Disposal Address) -->
          <div class="locations-row">
            <!-- Load Address -->
            <div class="location-box load-box">
              <div class="location-header">
                <span class="location-icon-badge load-badge">
                  <Location size={13} />
                  <span>LOAD ADDRESS</span>
                </span>
                <span class="address-req-tag">Street &amp; Suburb Required</span>
              </div>
              <div class="geocoder-embed-wrap">
                {#key `${getMaterialKey(selectedMaterial)}-load-geo`}
                  <Geocoder
                    accessToken={MAPBOX_TOKEN}
                    on:result={handleTabLoadAddressResult}
                    on:clear={handleTabLoadAddressClear}
                    value={selectedLoadAddressStr}
                    geocoder
                    options={{
                      countries: "AU",
                      types: "address",
                      placeholder: "Search site address (e.g. Church St, Parramatta)...",
                    }}
                  />
                {/key}
              </div>
              {#if tabLoadAddressWarning}
                <div class="address-geo-warning">
                  <WarningFilled size={13} />
                  <span>{tabLoadAddressWarning}</span>
                </div>
              {/if}
            </div>

            <!-- Route Connector Arrow -->
            <div class="route-arrow-divider" aria-hidden="true" title="Haulage Route Direction">
              <ArrowRight size={16} />
            </div>

            <!-- Disposal Address -->
            <div class="location-box disposal-box">
              <div class="location-header">
                <span class="location-icon-badge disposal-badge">
                  <Location size={13} />
                  <span>DISPOSAL ADDRESS</span>
                </span>
                <span class="address-req-tag">Street &amp; Suburb Required</span>
              </div>
              <div class="geocoder-embed-wrap">
                {#key `${getMaterialKey(selectedMaterial)}-disp-geo`}
                  <Geocoder
                    accessToken={MAPBOX_TOKEN}
                    on:result={handleTabDisposalAddressResult}
                    on:clear={handleTabDisposalAddressClear}
                    value={selectedDisposalAddressStr}
                    geocoder
                    options={{
                      countries: "AU",
                      types: "address",
                      placeholder: "Search disposal address (e.g. 50 Wallgrove Rd, Eastern Creek)...",
                    }}
                  />
                {/key}
              </div>
              {#if tabDisposalAddressWarning}
                <div class="address-geo-warning">
                  <WarningFilled size={13} />
                  <span>{tabDisposalAddressWarning}</span>
                </div>
              {/if}
            </div>
          </div>

          <!-- Disposal Yard & Contact Details Row -->
          <div class="yard-info-row">
            <!-- Yard Brand Identity (Logo + Name + Quick Yard Select) -->
            <div class="yard-brand-group">
              <div class="yard-logo-container">
                {#if resolvedYard?.image}
                  <img
                    class="yard-logo-image"
                    src={"./assests/yards/" + resolvedYard.image}
                    alt={resolvedYard.name || "Disposal Yard"}
                    on:error={(e) => {
                      reportYardImageError(resolvedYard, "not_found", "./assests/yards/" + resolvedYard.image);
                      e.currentTarget.style.display = "none";
                      const fb = e.currentTarget.parentElement.querySelector(".yard-logo-placeholder");
                      if (fb) fb.style.display = "flex";
                    }}
                  />
                  <div class="yard-logo-image yard-logo-placeholder" style="display: none;">🏭</div>
                {:else}
                  <div class="yard-logo-image yard-logo-placeholder" style="display: flex;">🏭</div>
                {/if}
              </div>
              <div class="yard-name-info">
                <div class="yard-header-line">
                  <span class="yard-badge-label">
                    <Enterprise size={11} />
                    <span>DISPOSAL YARD</span>
                  </span>
                  <select
                    class="yard-quick-select"
                    title="Select registered yard"
                    value={selectedMaterial?.yard_id ?? (typeof selectedMaterial?.yard === 'object' ? selectedMaterial?.yard?.id : selectedMaterial?.yard) ?? ""}
                    on:change={handleTabYardSelectChange}
                  >
                    <option value="">-- Pick Registered Yard --</option>
                    {#each $yards || [] as y}
                      <option value={y.id}>{y.name} {y.suburb || y.address?.suburb ? `(${y.suburb || y.address?.suburb})` : ""}</option>
                    {/each}
                    <option value="custom">✏️ Custom Yard (Manual)</option>
                  </select>
                </div>
                <div class="yard-inputs-row">
                  <input
                    type="text"
                    class="site-detail-input yard-name-input"
                    placeholder="Yard / Facility Name"
                    value={selectedMaterial?.yard_name || resolvedYard?.name || ""}
                    on:input={(e) => handleTabYardNameInput(e.target.value)}
                  />
                  <input
                    type="text"
                    class="site-detail-input yard-suburb-input"
                    placeholder="Suburb (e.g. St Marys)"
                    value={selectedMaterial?.suburb || resolvedYard?.suburb || ""}
                    on:input={(e) => handleTabYardSuburbInput(e.target.value)}
                  />
                </div>
                {#if resolvedYardLiveUrl}
                  <div class="yard-live-link-box">
                    <span class="live-link-tag">
                      <span class="live-pulse-dot"></span>
                      <span>YARD LIVE:</span>
                    </span>
                    <a
                      href={resolvedYardLiveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="live-link-anchor"
                      title="Open yard live tracking view in new tab"
                    >
                      <span class="live-url-text">{resolvedYardLiveUrl}</span>
                      <Launch size={11} class="launch-icon" />
                    </a>
                    <button
                      type="button"
                      class="live-copy-btn {copiedLinkType === 'yard' ? 'copied' : ''}"
                      title="Copy yard tracking URL"
                      on:click={() => handleCopyLiveLink(resolvedYardLiveUrl, 'yard')}
                    >
                      {#if copiedLinkType === 'yard'}
                        <Checkmark size={11} />
                        <span>Copied!</span>
                      {:else}
                        <Copy size={11} />
                        <span>Copy</span>
                      {/if}
                    </button>
                  </div>
                {/if}
              </div>
            </div>

            <!-- Main Contact Person -->
            <div class="yard-contact-card">
              <div class="contact-header">
                <span class="contact-badge-label">
                  <User size={11} />
                  <span>MAIN CONTACT</span>
                </span>
                <input
                  type="text"
                  class="site-detail-input contact-position-input"
                  placeholder="Role (e.g. Weighbridge)"
                  value={tabYardContactPosition}
                  on:input={(e) => updateTabContactField("position", e.target.value)}
                />
              </div>
              <div class="contact-body">
                <div class="contact-input-row">
                  <User size={12} class="contact-input-icon" />
                  <input
                    type="text"
                    class="site-detail-input contact-field-input"
                    placeholder="Contact Name (e.g. Steve)"
                    value={tabYardContactName}
                    on:input={(e) => updateTabContactField("name", e.target.value)}
                  />
                </div>
                <div class="contact-sub-row">
                  <div class="contact-input-row contact-phone-wrap">
                    <Phone size={12} class="contact-input-icon" />
                    <input
                      type="tel"
                      class="site-detail-input contact-field-input"
                      placeholder="Phone (e.g. 0417 458 178)"
                      value={tabYardContactPhone}
                      on:input={(e) => updateTabContactField("phone", e.target.value)}
                    />
                  </div>
                  <div class="contact-input-row contact-email-wrap">
                    <Email size={12} class="contact-input-icon" />
                    <input
                      type="email"
                      class="site-detail-input contact-field-input"
                      placeholder="Email (e.g. dispatch@yard.com)"
                      value={tabYardContactEmail}
                      on:input={(e) => updateTabContactField("email", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      {/if}
    </div>

    <!-- Right Side: Turnaround Schedule & Fleet Capacities Panel -->
    <div class="allocation-top-right">
      {#if activeTurnaroundMat}
        {@const sched = currentSchedule}
        <TurnaroundSchedulePanel
          bind:material={activeTurnaroundMat}
          isEditMode={true}
          showHaulageDuration={true}
          daysCount={sched ? sched.daysCount : 1}
          on:change={handleTurnaroundPanelChange}
          on:days-count-change={(e) => {
            if (e.detail?.delta !== undefined) {
              setDaysCount(e.detail.delta);
            } else if (e.detail?.daysCount !== undefined) {
              setDaysCountExplicit(e.detail.daysCount);
            }
          }}
        />
      {/if}
    </div>
  </div>

  <!-- Allocation Conflict Warning Alert Banner -->
  {#if allocationConflictAlert}
    <div class="allocation-conflict-alert" transition:slide={{ duration: 250 }}>
      <div class="conflict-alert-icon">
        <WarningFilled size={22} />
      </div>
      <div class="conflict-alert-body">
        <div class="conflict-alert-title">
          <strong>Fleet Allocation Conflict &mdash; Truck {allocationConflictAlert.truckId}</strong>
        </div>
        <div class="conflict-alert-desc">
          {allocationConflictAlert.reason}
        </div>
      </div>
      <div class="conflict-alert-actions">
        {#if allocationConflictAlert.job}
          <button 
            type="button" 
            class="conflict-view-job-btn" 
            on:click={() => handleOpenConflictingJob(allocationConflictAlert)}
            title={`View Job #${allocationConflictAlert.jobId}`}
          >
            <span>View Job #{allocationConflictAlert.jobId}</span>
            <ArrowRight size={14} />
          </button>
        {/if}
        <button 
          type="button" 
          class="conflict-dismiss-btn" 
          on:click={clearConflictAlert}
          title="Dismiss warning"
        >
          <Close size={16} />
        </button>
      </div>
    </div>
  {/if}

  <!-- Daily Columns Board (Drag & Drop Target Area) - Full width below top section -->
  {#if selectedMaterial && currentSchedule}
    {@const sched = currentSchedule}
    <div class="daily-columns-container">
      {#each Array(sched.daysCount) as _, dayIndex}
        {@const dayTrucks = sched.days[dayIndex] || []}
        {@const dayDateStr = computeDayDate(startDate, dayIndex)}
        {@const totalCap = getDayTotalCapacity(dayTrucks)}
        {@const isOver = dragOverDayIndex === dayIndex}

        <div 
          class="day-column {isOver ? (dragOverConflict ? 'drop-target-conflict' : 'drop-target-active') : ''}"
          on:dragover={(e) => handleDragOver(e, dayIndex)}
          on:dragenter={(e) => handleDragOver(e, dayIndex)}
          on:dragleave={(e) => handleDragLeave(e, dayIndex)}
          on:drop={(e) => handleDrop(e, dayIndex)}
          role="region"
          aria-label={`Day ${dayIndex + 1} truck allocation`}
        >
          <!-- Column Header -->
          <div class="day-col-header">
            <div class="day-title-row">
              <span class="day-badge">Day {dayIndex + 1}</span>
              <span class="day-date">{dayDateStr}</span>
            </div>

            <div class="day-metrics-row">
              <span class="truck-count-chip">
                <DeliveryTruck size={12} />
                <span>{dayTrucks.length} truck{dayTrucks.length !== 1 ? 's' : ''}</span>
              </span>
              {#if totalCap > 0}
                <span class="cap-chip">{totalCap}t cap</span>
              {/if}
              {#if dayTrucks.length > 0}
                <div class="day-actions-group">
                  {#if dayIndex < 59}
                    <button 
                      class="copy-day-btn" 
                      type="button" 
                      title={`Copy all ${dayTrucks.length} truck${dayTrucks.length !== 1 ? 's' : ''} to Day ${dayIndex + 2}`}
                      on:click={() => copyDayToNextDay(dayIndex)}
                    >
                      <span>Day {dayIndex + 2}</span>
                      <ChevronRight size={12} />
                    </button>
                  {/if}
                  <button 
                    class="clear-day-btn" 
                    type="button" 
                    title="Clear all trucks from this day"
                    on:click={() => clearDaySchedule(dayIndex)}
                  >
                    <TrashCan size={12} />
                  </button>
                </div>
              {/if}
            </div>
          </div>

          <!-- Drop Zone / Allocated Trucks List -->
          <div class="day-col-body">
            {#if dayTrucks.length === 0}
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div 
                class="empty-drop-zone"
                role="button"
                tabindex="0"
                on:click={() => isFleetDrawerOpen = true}
                on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') isFleetDrawerOpen = true; }}
                title="Click to open fleet trucks drawer or drag trucks here"
              >
                <Draggable size={24} class="drop-icon" />
                <span class="drop-prompt">Drop trucks here</span>
                <span class="drop-sub">Drag vehicle from fleet drawer</span>
              </div>
            {:else}
              <div class="allocated-trucks-stack">
                {#each dayTrucks as item, truckIdx (item.truck?.id || truckIdx)}
                  {@const attachedTrailer = item.trailer || item.truck?.default_trailer || item.truck?.trailer}
                  {@const totalCapTonnes = (Number(item.truck?.capacity || 17) + Number(attachedTrailer?.capacity || 0))}
                  {@const conflict = getTruckConflict(item.truck?.id, dayIndex)}
                  <div 
                    class="scheduled-truck-card interactive-card"
                    class:has-fleet-conflict={!!conflict}
                    class:is-drag-target={dragOverAllocatedTruck && dragOverAllocatedTruck.dayIndex === dayIndex && dragOverAllocatedTruck.truckIndex === truckIdx}
                    on:dragover={(e) => handleAllocatedDragOver(e, dayIndex, truckIdx)}
                    on:dragleave={(e) => handleAllocatedDragLeave(e, dayIndex, truckIdx)}
                    on:drop={(e) => handleAllocatedDrop(e, dayIndex, truckIdx)}
                    on:click={() => openTruckDetailModal(dayIndex, truckIdx, item)}
                    role="button"
                    tabindex="0"
                    on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') openTruckDetailModal(dayIndex, truckIdx, item); }}
                    title={conflict ? `${conflict.reason} Click to edit details.` : "Click to view & edit allocation details, or drag handle to reorder"}
                  >
                    <!-- Left: Drag Handle with square dots (2.5px) -->
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <div 
                      class="card-drag-handle allocated-drag-handle" 
                      role="presentation"
                      draggable="true"
                      title="Drag to reorder departure sequence or move between days"
                      on:click|stopPropagation
                      on:dragstart={(e) => handleAllocatedDragStart(e, dayIndex, truckIdx, item)}
                      on:dragend={handleDragEnd}
                    >
                      <div class="drag-dots-col" aria-hidden="true">
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        <span class="dot"></span><span class="dot"></span>
                        {#if attachedTrailer}
                          <span class="dot"></span><span class="dot"></span>
                          <span class="dot"></span><span class="dot"></span>
                          <span class="dot"></span><span class="dot"></span>
                        {/if}
                      </div>
                    </div>

                    <!-- Right: Content Column -->
                    <div class="scheduled-card-content-col">
                      <!-- Scheduled Departure Time Badge & Steppers -->
                      <div class="truck-card-time-row">
                        <span class="scheduled-time-pill" title="Calculated departure time from depot / load site">
                          <Time size={12} />
                          <strong>{formatDisplayTime(item.scheduledStartTime)}</strong>
                        </span>

                        {#if truckIdx === 0}
                          <span class="sequence-tag first-tag">1st Departure</span>
                        {:else}
                          <span class="sequence-tag">+{sched.loadInterval * truckIdx}m</span>
                        {/if}

                        <button 
                          class="remove-truck-btn" 
                          type="button" 
                          title="Remove truck from this day"
                          on:click|stopPropagation={() => removeTruckFromDay(dayIndex, truckIdx)}
                        >
                          <Close size={14} />
                        </button>
                      </div>

                      <!-- Conflict Warning Pill if allocated on another job -->
                      {#if conflict}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <div 
                          class="card-conflict-pill" 
                          title={conflict.reason} 
                          on:click|stopPropagation={() => handleOpenConflictingJob(conflict)}
                          role="button"
                          tabindex="0"
                          on:keydown={(e) => { if (e.key === 'Enter') handleOpenConflictingJob(conflict); }}
                        >
                          <WarningFilled size={11} class="conflict-warn-icon" />
                          <span class="conflict-job-text">
                            {conflict.type === 'material' ? `Double-booked: ${conflict.materialName}` : `Double-booked: #${conflict.jobId}`}
                          </span>
                        </div>
                      {/if}

                      <!-- Truck & Driver Info -->
                      <div class="truck-card-main">
                        <div class="truck-id-line">
                          <span class="truck-rego">{item.truck?.id || "TRUCK"}</span>
                          <span class="truck-type-lbl">{item.truck?.type || "Vehicle"}</span>
                          <span class="truck-capacity-lbl">{totalCapTonnes}t</span>
                        </div>

                        {#if attachedTrailer}
                          <div class="truck-trailer-line">
                            <span class="trailer-sub-badge" title={`Attached Trailer: ${attachedTrailer.id}`}>
                              + Trailer: <strong>{attachedTrailer.id}</strong> ({attachedTrailer.type}, {attachedTrailer.capacity}t)
                            </span>
                          </div>
                        {/if}

                        <div class="driver-line">
                          <div class="driver-details-wrap">
                            <div class="driver-info-group">
                              <User size={12} class="driver-icon" />
                              <span class="driver-name">{item.driver?.name || item.truck?.default_driver?.name || "Assigned Driver"}</span>
                            </div>
                            {#if item.driver?.phone || item.truck?.default_driver?.phone}
                              <div class="driver-phone-group" title="Driver Phone Number">
                                <Phone size={11} class="driver-phone-icon" />
                                <span class="driver-phone-text">{formatPhoneNumber(item.driver?.phone || item.truck?.default_driver?.phone)}</span>
                              </div>
                            {/if}
                          </div>
                          <button 
                            class="card-edit-icon-btn" 
                            type="button" 
                            title="Edit allocation details"
                            on:click|stopPropagation={() => openTruckDetailModal(dayIndex, truckIdx, item)}
                          >
                            <Edit size={13} />
                          </button>
                        </div>
                      </div>

                      <!-- Optional Day Navigation (only when project has multiple haulage days) -->
                      {#if sched.daysCount > 1 && (dayIndex > 0 || dayIndex < sched.daysCount - 1)}
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <div class="truck-card-bottom-row" role="presentation" on:click|stopPropagation>
                          <div class="day-nav-group">
                            {#if dayIndex > 0}
                              <button 
                                class="move-day-btn" 
                                type="button" 
                                title="Move to previous day"
                                on:click|stopPropagation={() => moveTruckBetweenDays(dayIndex, truckIdx, dayIndex - 1)}
                              >
                                <ChevronLeft size={12} />
                                <span>Day {dayIndex}</span>
                              </button>
                            {/if}
                            {#if dayIndex < sched.daysCount - 1}
                              <button 
                                class="move-day-btn" 
                                type="button" 
                                title="Move to next day"
                                on:click|stopPropagation={() => moveTruckBetweenDays(dayIndex, truckIdx, dayIndex + 1)}
                              >
                                <span>Day {dayIndex + 2}</span>
                                <ChevronRight size={12} />
                              </button>
                            {/if}
                          </div>
                        </div>
                      {/if}
                    </div>
                  </div>
                {/each}

                <!-- Drop slot at bottom of column stack to append truck -->
                <div 
                  class="stack-bottom-drop-zone"
                  class:is-active={dragOverAllocatedTruck && dragOverAllocatedTruck.dayIndex === dayIndex && dragOverAllocatedTruck.truckIndex === dayTrucks.length}
                  on:dragover={(e) => handleAllocatedDragOver(e, dayIndex, dayTrucks.length)}
                  on:dragleave={(e) => handleAllocatedDragLeave(e, dayIndex, dayTrucks.length)}
                  on:drop={(e) => handleAllocatedDrop(e, dayIndex, dayTrucks.length)}
                  title="Drop here to append truck to Day {dayIndex + 1}"
                >
                  <span class="bottom-drop-text">+ Drop truck here</span>
                </div>
              </div>
            {/if}
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<!-- Slide-Out Fleet Trucks Panel (Positioned on the Right, without screen-blocking backdrop) -->
{#if isFleetDrawerOpen}
  <aside 
    class="fleet-drawer-panel" 
    transition:fly={{ x: 420, duration: 250 }}
  >
    <!-- Drawer Header -->
    <div class="drawer-header">
      <div class="drawer-title-row">
        <div class="drawer-heading-group">
          <DeliveryTruck size={20} class="truck-drawer-icon" />
          <h4 class="drawer-heading">Available Fleet Trucks</h4>
        </div>
        <button 
          class="drawer-close-btn" 
          type="button" 
          on:click={() => isFleetDrawerOpen = false}
          title="Close Fleet Drawer"
        >
          <Close size={18} />
        </button>
      </div>

      <p class="drawer-instruction">
        Drag trucks into any Day column, or click <strong>+ Day</strong> to assign instantly.
      </p>

      <!-- In-Drawer Conflict Alert -->
      {#if allocationConflictAlert}
        <div class="drawer-conflict-alert" transition:slide={{ duration: 200 }}>
          <div class="drawer-conflict-header">
            <div class="drawer-conflict-title">
              <span class="drawer-conflict-icon"><WarningFilled size={16} /></span>
              <strong>Conflict: {allocationConflictAlert.truckId}</strong>
            </div>
            <button 
              type="button" 
              class="drawer-conflict-dismiss" 
              on:click={clearConflictAlert}
              title="Dismiss"
            >
              <Close size={14} />
            </button>
          </div>
          <p class="drawer-conflict-msg">{allocationConflictAlert.reason}</p>
          {#if allocationConflictAlert.job}
            <button 
              type="button" 
              class="drawer-conflict-view-btn"
              on:click={() => handleOpenConflictingJob(allocationConflictAlert)}
            >
              <span>View Job #{allocationConflictAlert.jobId}</span>
              <ArrowRight size={13} />
            </button>
          {/if}
        </div>
      {/if}

      <!-- Search input -->
      <div class="drawer-search-box">
        <Search size={15} class="search-ico" />
        <input 
          type="text" 
          placeholder="Search rego, driver, type..." 
          bind:value={searchTruckQuery} 
          class="drawer-search-input"
        />
        {#if searchTruckQuery}
          <button class="clear-search-btn" on:click={() => searchTruckQuery = ""} type="button">&times;</button>
        {/if}
      </div>

      <!-- Filter pills -->
      <div class="drawer-filters">
        <button 
          class="type-pill {truckTypeFilter === 'all' ? 'active' : ''}" 
          type="button" 
          on:click={() => truckTypeFilter = 'all'}
        >
          All ({fleetTrucks.length})
        </button>
        <button 
          class="type-pill {truckTypeFilter === '6 wheeler' ? 'active' : ''}" 
          type="button" 
          on:click={() => truckTypeFilter = '6 wheeler'}
        >
          6 wheeler
        </button>
        <button 
          class="type-pill {truckTypeFilter === '8 wheeler' ? 'active' : ''}" 
          type="button" 
          on:click={() => truckTypeFilter = '8 wheeler'}
        >
          8 wheeler
        </button>
        <button 
          class="type-pill {truckTypeFilter === '10 wheeler' ? 'active' : ''}" 
          type="button" 
          on:click={() => truckTypeFilter = '10 wheeler'}
        >
          10 wheeler
        </button>
      </div>

      <!-- Add Truck Action Bar -->
      <div class="drawer-add-action-bar">
        <button 
          class="add-truck-toggle-btn {isAddingTruck ? 'active' : ''}" 
          type="button" 
          on:click={() => { isAddingTruck = !isAddingTruck; if (isAddingTruck) addTruckError = ''; }}
          title={isAddingTruck ? "Close add truck form" : "Register a new vehicle to fleet"}
        >
          {#if isAddingTruck}
            <Close size={14} />
            <span>Cancel</span>
          {:else}
            <Add size={15} />
            <span>+ Add Truck</span>
          {/if}
        </button>

        {#if recentlyAddedTruckId}
          <div class="recent-truck-pill" transition:fade={{ duration: 150 }}>
            <Checkmark size={13} />
            <span>Added <strong>{recentlyAddedTruckId}</strong></span>
          </div>
        {/if}
      </div>

      <!-- Expandable Add Truck Form Panel -->
      {#if isAddingTruck}
        <div class="add-truck-form-panel" transition:slide={{ duration: 200 }}>
          <div class="add-form-title-row">
            <DeliveryTruck size={16} />
            <span class="add-form-title">Register New Fleet Truck</span>
          </div>

          <div class="add-form-fields">
            <div class="add-form-row">
              <div class="add-field-group field-half">
                <label for="new-truck-rego">Truck ID / Rego <span class="req">*</span></label>
                <input 
                  id="new-truck-rego" 
                  type="text" 
                  placeholder="e.g. TRK-09" 
                  bind:value={newTruckId} 
                  maxlength="15"
                  class="add-form-input text-uppercase"
                  on:keydown={(e) => { if (e.key === 'Enter') handleAddTruckSubmit(); }}
                />
              </div>

              <div class="add-field-group field-half">
                <label for="new-truck-type-select">Type</label>
                <select 
                  id="new-truck-type-select" 
                  bind:value={newTruckType} 
                  on:change={(e) => handleNewTruckTypeChange(e.target.value)}
                  class="add-form-input"
                >
                  <option value="6 wheeler">6 wheeler (13t default)</option>
                  <option value="8 wheeler">8 wheeler (17t default)</option>
                  <option value="10 wheeler">10 wheeler (21t default)</option>
                </select>
              </div>
            </div>

            <div class="add-form-row">
              <div class="add-field-group field-third">
                <label for="new-truck-cap">Truck Payload (t)</label>
                <input 
                  id="new-truck-cap" 
                  type="number" 
                  min="1" 
                  max="100" 
                  bind:value={newTruckCapacity} 
                  class="add-form-input" 
                />
              </div>

              <div class="add-field-group field-two-thirds">
                <label for="new-truck-driver-name">Driver Name</label>
                <input 
                  id="new-truck-driver-name" 
                  type="text" 
                  placeholder="e.g. Dave Miller" 
                  bind:value={newTruckDriverName} 
                  class="add-form-input" 
                  on:keydown={(e) => { if (e.key === 'Enter') handleAddTruckSubmit(); }}
                />
              </div>
            </div>

            <div class="add-form-row">
              <div class="add-field-group field-full">
                <label for="new-truck-driver-phone">Driver Phone (optional)</label>
                <PhoneInput 
                  id="new-truck-driver-phone" 
                  bind:value={newTruckDriverPhone} 
                  placeholder="0412 345 678" 
                />
              </div>
            </div>

            <!-- Trailer Attachment Section -->
            <div class="add-trailer-toggle-row">
              <label class="trailer-toggle-label">
                <input 
                  type="checkbox" 
                  bind:checked={attachTrailer} 
                  class="trailer-checkbox"
                />
                <span class="trailer-toggle-text">Attach Interchangeable Trailer</span>
              </label>
            </div>

            {#if attachTrailer}
              <div class="trailer-subform-box" transition:slide={{ duration: 150 }}>
                <div class="trailer-subform-title">
                  <span>Trailer Specifications</span>
                </div>
                <div class="add-form-row">
                  <div class="add-field-group field-half">
                    <label for="new-trailer-rego">Trailer Rego / Plate <span class="req">*</span></label>
                    <input 
                      id="new-trailer-rego" 
                      type="text" 
                      placeholder="e.g. TRL-01" 
                      bind:value={newTrailerId} 
                      maxlength="15"
                      class="add-form-input text-uppercase"
                      on:keydown={(e) => { if (e.key === 'Enter') handleAddTruckSubmit(); }}
                    />
                  </div>

                  <div class="add-field-group field-half">
                    <label for="new-trailer-type-select">Trailer Type</label>
                    <select 
                      id="new-trailer-type-select" 
                      bind:value={newTrailerType} 
                      on:change={(e) => handleNewTrailerTypeChange(e.target.value)}
                      class="add-form-input"
                    >
                      <option value="4 wheeler">4 wheeler (12t default)</option>
                      <option value="6 wheeler">6 wheeler (16t default)</option>
                      <option value="8 wheeler">8 wheeler (20t default)</option>
                      <option value="10 wheeler">10 wheeler (24t default)</option>
                    </select>
                  </div>
                </div>

                <div class="add-form-row">
                  <div class="add-field-group field-half">
                    <label for="new-trailer-cap">Trailer Payload (t)</label>
                    <input 
                      id="new-trailer-cap" 
                      type="number" 
                      min="1" 
                      max="60" 
                      bind:value={newTrailerCapacity} 
                      class="add-form-input" 
                    />
                  </div>

                  <div class="add-field-group field-half combined-preview-cell">
                    <span class="combined-label">Combined Payload:</span>
                    <span class="combined-value">
                      <strong>{(Number(newTruckCapacity) || 0) + (Number(newTrailerCapacity) || 0)}t</strong>
                      <small>({newTruckCapacity}t + {newTrailerCapacity}t)</small>
                    </span>
                  </div>
                </div>
              </div>
            {/if}
          </div>

          {#if addTruckError}
            <div class="add-form-error" transition:fade={{ duration: 150 }}>
              {addTruckError}
            </div>
          {/if}

          <div class="add-form-btn-row">
            <button type="button" class="btn-cancel-add" on:click={resetAddTruckForm}>
              Cancel
            </button>
            <button 
              type="button" 
              class="btn-submit-add" 
              disabled={isSavingTruck || !newTruckId.trim()} 
              on:click={handleAddTruckSubmit}
            >
              {#if isSavingTruck}
                <span class="loading-spin-small"></span>
                <span>Saving...</span>
              {:else}
                <Checkmark size={14} />
                <span>Save to Fleet</span>
              {/if}
            </button>
          </div>
        </div>
      {/if}
    </div>

    <!-- Draggable Trucks List -->
    <div class="drawer-trucks-list">
      {#if isLoadingFleet}
        <div class="drawer-loading">
          <span class="loading-spin"></span>
          <span>Loading fleet records...</span>
        </div>
      {:else if filteredFleet.length === 0}
        <div class="drawer-empty">
          <DeliveryTruck size={32} />
          <span>No fleet vehicles matched your search.</span>
        </div>
      {:else}
        {#each filteredFleet as truck (truck.id)}
          {@const isScheduled = isTruckScheduled(currentSchedule, truck.id)}
          <div 
            class="draggable-truck-card {isScheduled ? 'card-scheduled' : ''} {recentlyAddedTruckId === truck.id ? 'card-just-added' : ''}"
            role="button"
            tabindex="0"
            title="Click to view & edit truck details, or drag handle to assign"
            on:click={() => openTruckDefaultConfigModal(truck)}
            on:keydown={(e) => { if (e.key === 'Enter' || e.key === ' ') openTruckDefaultConfigModal(truck); }}
          >
            <!-- Left: Drag Handle with square dots extending down to bottom of Assign text -->
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div 
              class="card-drag-handle" 
              role="presentation"
              draggable="true"
              title="Drag into any Day column to assign"
              on:click|stopPropagation
              on:dragstart={(e) => handleDragStart(e, truck)}
              on:dragend={handleDragEnd}
            >
              <div class="drag-dots-col" aria-hidden="true">
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                <span class="dot"></span><span class="dot"></span>
                {#if truck.default_trailer || truck.trailer}
                  <span class="dot"></span><span class="dot"></span>
                  <span class="dot"></span><span class="dot"></span>
                  <span class="dot"></span><span class="dot"></span>
                  <span class="dot"></span><span class="dot"></span>
                {/if}
              </div>
            </div>

            <!-- Right: Content Column (Identity + Optional Trailer + Assign Toolbar) -->
            <div class="card-content-col">
              <!-- Row 1: Vehicle & Driver Identity -->
              <div class="card-identity-row">
                <div class="identity-left">
                  <strong class="truck-rego-tag">{truck.id}</strong>
                  <span class="truck-badge type-badge">{truck.type || "6 wheeler"}</span>
                  <span class="truck-badge cap-badge">{(truck.total_capacity || truck.capacity) ? `${truck.total_capacity || truck.capacity}t` : "17t"}</span>
                </div>

                <div class="identity-driver" title={`Default Driver: ${truck.default_driver?.name || 'Standard Driver'}`}>
                  <User size={12} class="driver-ico" />
                  <span class="driver-name-text">{truck.default_driver?.name || "Driver"}</span>
                </div>
              </div>

              <!-- Optional Attached Trailer Row -->
              {#if truck.default_trailer || truck.trailer}
                {@const trl = truck.default_trailer || truck.trailer}
                <div class="card-trailer-row">
                  <span class="trailer-badge">
                    + Trailer: <strong>{trl.id}</strong> ({trl.type}, {trl.capacity}t)
                  </span>
                  <span class="trailer-breakdown">
                    ({truck.capacity}t + {trl.capacity}t)
                  </span>
                </div>
              {/if}

              <!-- Row 2: Day Allocation Quick Toolbar -->
              <!-- svelte-ignore a11y-click-events-have-key-events -->
              <div 
                class="card-action-row" 
                role="presentation"
                on:mousedown|stopPropagation
                on:click|stopPropagation
              >
                <div class="alloc-prompt">
                  <Calendar size={12} class="alloc-ico" />
                  <span>ASSIGN: &rarr;</span>
                </div>

                <div class="quick-days-pills">
                  {#each Array(currentSchedule ? currentSchedule.daysCount : 1) as _, dIdx}
                    {@const inDay = isTruckInDay(currentSchedule, truck.id, dIdx)}
                    {@const conflict = !inDay ? getTruckConflict(truck.id, dIdx) : null}
                    {#if inDay}
                      <button 
                        class="day-status-pill pill-assigned pill-btn-remove" 
                        type="button" 
                        title={`Click to remove ${truck.id} from Day ${dIdx + 1}`}
                        on:mousedown|stopPropagation
                        on:click|stopPropagation={(e) => { e.preventDefault(); removeTruckFromDayByTruckId(truck.id, dIdx); }}
                      >
                        <span class="pill-icon-normal"><Checkmark size={11} /></span>
                        <span class="pill-icon-hover"><Close size={11} /></span>
                        <span>{currentSchedule && currentSchedule.daysCount > 1 ? `D${dIdx + 1}` : 'Day 1'}</span>
                      </button>
                    {:else if conflict}
                      <button 
                        class="day-status-pill pill-conflict" 
                        type="button" 
                        aria-disabled="true"
                        title={`Unavailable: ${truck.id} is already allocated on ${conflict.dateFormatted} to ${conflict.jobTitle}. Click to view details.`}
                        on:mousedown|stopPropagation
                        on:click|stopPropagation={(e) => { e.preventDefault(); showConflictAlert(conflict); }}
                      >
                        <Close size={11} />
                        <span>{currentSchedule && currentSchedule.daysCount > 1 ? `D${dIdx + 1}` : 'Day 1'}</span>
                      </button>
                    {:else}
                      <button 
                        class="day-status-pill pill-btn-add" 
                        type="button" 
                        title={`Allocate ${truck.id} to Day ${dIdx + 1}`}
                        on:mousedown|stopPropagation
                        on:click|stopPropagation={(e) => { e.preventDefault(); allocateTruckToDay(truck, dIdx); }}
                      >
                        <Add size={11} />
                        <span>{currentSchedule && currentSchedule.daysCount > 1 ? `D${dIdx + 1}` : 'Day 1'}</span>
                      </button>
                    {/if}
                  {/each}
                </div>
              </div>
            </div>
          </div>
        {/each}
      {/if}
    </div>
  </aside>
{/if}

<!-- Truck Detail & Allocation Modal (Triggered by clicking a truck in a day column or in fleet drawer) -->
{#if isDetailModalOpen && detailAllocationItem}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div 
    class="modal-backdrop" 
    transition:fade={{ duration: 150 }} 
    on:click={() => isDetailModalOpen = false}
    role="presentation"
  >
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div 
      class="truck-detail-modal-card" 
      transition:fly={{ y: 20, duration: 200 }} 
      on:click|stopPropagation
      role="dialog"
      aria-modal="true"
      aria-labelledby="truck-detail-title"
    >
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="modal-title-group">
          <DeliveryTruck size={24} class="modal-truck-icon" />
          <div>
            <h3 id="truck-detail-title" class="modal-title">
              {detailModalMode === 'truck-default' ? 'Truck Details' : 'Truck Allocation Details'}
            </h3>
            <p class="modal-subtitle">
              {#if detailModalMode === 'truck-default'}
                Rego: <strong>{detailAllocationItem.truck?.id}</strong> ({detailAllocationItem.truck?.type || 'Vehicle'}) &bull; Fleet Default Settings
              {:else}
                Day {(detailDayIndex ?? 0) + 1} &bull; Rego: <strong>{detailAllocationItem.truck?.id}</strong> ({detailAllocationItem.truck?.type || '6 wheeler'})
              {/if}
            </p>
          </div>
        </div>
        <button 
          class="modal-close-btn" 
          type="button" 
          on:click={() => isDetailModalOpen = false}
          title="Close details"
        >
          <Close size={18} />
        </button>
      </div>

      <!-- Modal Body -->
      <div class="modal-body">
        {#if detailModalError}
          <div class="detail-error-banner" transition:fade={{ duration: 150 }}>
            {detailModalError}
          </div>
        {/if}

        <!-- Section 1: Driver Assignment / Default Driver -->
        <div class="detail-section">
          <div class="section-title-row">
            <h4 class="section-title">
              <User size={16} />
              <span>{detailModalMode === 'truck-default' ? 'Default Driver' : 'Driver Assignment'}</span>
            </h4>
          </div>
          <p class="section-desc">
            {#if detailModalMode === 'truck-default'}
              Set the default driver for this truck across all allocations and runs.
            {:else}
              Assign or modify the driver specifically for this truck's run on Day {(detailDayIndex ?? 0) + 1}.
            {/if}
          </p>

          <div class="detail-grid-row">
            <div class="detail-input-group">
              <label for="edit-driver-name">Driver Name</label>
              <input 
                id="edit-driver-name" 
                type="text" 
                class="detail-text-input" 
                placeholder="e.g. John Smith" 
                bind:value={editDriverName} 
              />
            </div>
            <div class="detail-input-group">
              <label for="edit-driver-phone">Phone Number</label>
              <PhoneInput 
                id="edit-driver-phone" 
                bind:value={editDriverPhone} 
                placeholder="0412 345 678" 
              />
            </div>
          </div>
        </div>

        <!-- Section 2: Interchangeable Trailer Attachment -->
        <div class="detail-section">
          <div class="section-title-row">
            <h4 class="section-title">
              <Layers size={16} />
              <span>{detailModalMode === 'truck-default' ? 'Default Trailer Attachment' : 'Interchangeable Trailer Attachment'}</span>
            </h4>
            {#if editTrailer && trailerEditMode === 'none'}
              <button 
                type="button" 
                class="btn-detach-trailer"
                on:click={() => { editTrailer = null; trailerEditMode = 'none'; }}
                title="Detach trailer from this truck"
              >
                Detach Trailer
              </button>
            {/if}
          </div>
          <p class="section-desc">
            {#if detailModalMode === 'truck-default'}
              Attach an interchangeable trailer as the default trailer for this truck.
            {:else}
              Attach an interchangeable trailer to increase this truck's haul capacity for this day.
            {/if}
          </p>

          {#if editTrailer && trailerEditMode === 'none'}
            <div class="current-trailer-display">
              <div class="current-trailer-left">
                <span class="trailer-pill-badge">ATTACHED</span>
                <strong class="trailer-id-text">{editTrailer.id}</strong>
                <span class="trailer-type-tag">{editTrailer.type || 'Trailer'}</span>
                <span class="trailer-cap-tag">+{editTrailer.capacity || 20}t</span>
              </div>
              <button 
                type="button" 
                class="btn-change-trailer"
                on:click={() => { trailerEditMode = 'existing'; }}
              >
                Change Trailer
              </button>
            </div>
          {:else if !editTrailer && trailerEditMode === 'none'}
            <div class="no-trailer-box">
              <span class="no-trailer-text">No trailer currently attached to this truck allocation.</span>
              <div class="trailer-action-buttons">
                <button 
                  type="button" 
                  class="btn-trailer-mode"
                  on:click={() => { trailerEditMode = 'existing'; }}
                >
                  Attach Existing Trailer
                </button>
                <button 
                  type="button" 
                  class="btn-trailer-mode btn-mode-primary"
                  on:click={() => { 
                    trailerEditMode = 'new';
                    newDetailTrailerCapacity = TRAILER_CONFIGS[newDetailTrailerType]?.defaultCapacity || 20;
                  }}
                >
                  + Create & Attach New Trailer
                </button>
              </div>
            </div>
          {/if}

          <!-- Attach Existing Trailer Subform -->
          {#if trailerEditMode === 'existing'}
            <div class="trailer-select-subform" transition:slide={{ duration: 150 }}>
              <div class="subform-header">
                <strong>Select from Registered Trailers:</strong>
                <button 
                  type="button" 
                  class="btn-cancel-link" 
                  on:click={() => { trailerEditMode = 'none'; }}
                >
                  Cancel
                </button>
              </div>
              
              {#if availableTrailersList.length === 0}
                <p class="empty-hint">No registered trailers found in the fleet. You can create one below.</p>
              {:else}
                <div class="detail-input-group" style="margin-top: 6px;">
                  <select class="detail-select-input" bind:value={selectedExistingTrailerId}>
                    <option value="">-- Choose a Trailer --</option>
                    {#each availableTrailersList as trl (trl.id)}
                      <option value={trl.id}>
                        {trl.id} &mdash; {trl.type} ({trl.capacity || 20}t)
                        {trl.attached_to_truck_id ? ` [Attached to ${trl.attached_to_truck_id}]` : ''}
                      </option>
                    {/each}
                  </select>
                </div>
              {/if}

              <div class="subform-footer-actions">
                <button 
                  type="button" 
                  class="btn-switch-mode"
                  on:click={() => { 
                    trailerEditMode = 'new'; 
                    newDetailTrailerCapacity = TRAILER_CONFIGS[newDetailTrailerType]?.defaultCapacity || 20;
                  }}
                >
                  + Register & Attach New Trailer
                </button>
              </div>
            </div>
          {/if}

          <!-- Create New Trailer Subform -->
          {#if trailerEditMode === 'new'}
            <div class="trailer-new-subform" transition:slide={{ duration: 150 }}>
              <div class="subform-header">
                <strong>Register New Interchangeable Trailer:</strong>
                <button 
                  type="button" 
                  class="btn-cancel-link" 
                  on:click={() => { trailerEditMode = 'none'; }}
                >
                  Cancel
                </button>
              </div>

              <div class="detail-grid-row" style="margin-top: 8px;">
                <div class="detail-input-group">
                  <label for="new-dtl-trl-id">Trailer Rego / Plate *</label>
                  <input 
                    id="new-dtl-trl-id" 
                    type="text" 
                    class="detail-text-input uppercase-input" 
                    placeholder="e.g. TRL-882" 
                    bind:value={newDetailTrailerId} 
                  />
                </div>
                <div class="detail-input-group">
                  <label for="new-dtl-trl-type">Trailer Type</label>
                  <select 
                    id="new-dtl-trl-type" 
                    class="detail-select-input"
                    bind:value={newDetailTrailerType}
                    on:change={() => {
                      if (TRAILER_CONFIGS[newDetailTrailerType]) {
                        newDetailTrailerCapacity = TRAILER_CONFIGS[newDetailTrailerType].defaultCapacity;
                      }
                    }}
                  >
                    {#each Object.entries(TRAILER_CONFIGS) as [tType, cfg]}
                      <option value={tType}>{tType} ({cfg.defaultCapacity}t default)</option>
                    {/each}
                  </select>
                </div>
              </div>

              <div class="detail-grid-row">
                <div class="detail-input-group">
                  <label for="new-dtl-trl-cap">Trailer Payload Capacity (t)</label>
                  <input 
                    id="new-dtl-trl-cap" 
                    type="number" 
                    min="1" 
                    max="60" 
                    class="detail-text-input" 
                    bind:value={newDetailTrailerCapacity} 
                  />
                </div>
                {#if availableTrailersList.length > 0}
                  <div class="detail-input-group action-align-end">
                    <button 
                      type="button" 
                      class="btn-switch-mode" 
                      on:click={() => { trailerEditMode = 'existing'; }}
                    >
                      &larr; Choose from existing trailers
                    </button>
                  </div>
                {/if}
              </div>
            </div>
          {/if}
        </div>

        <!-- Section 3: Combined Capacity Calculation Summary -->
        <div class="detail-summary-card">
          <div class="summary-breakdown">
            <div class="summary-item">
              <span class="item-label">Truck Capacity</span>
              <strong class="item-val">{detailAllocationItem.truck?.capacity || 17}t</strong>
            </div>
            <span class="calc-symbol">+</span>
            <div class="summary-item">
              <span class="item-label">Trailer Capacity</span>
              <strong class="item-val">
                {#if trailerEditMode === 'new'}
                  {Number(newDetailTrailerCapacity) || 0}t
                {:else if trailerEditMode === 'existing'}
                  {Number(availableTrailersList.find(t => t.id === selectedExistingTrailerId)?.capacity) || 0}t
                {:else if editTrailer}
                  {Number(editTrailer.capacity) || 0}t
                {:else}
                  0t
                {/if}
              </strong>
            </div>
            <span class="calc-symbol">=</span>
            <div class="summary-item total-item">
              <span class="item-label">{detailModalMode === 'truck-default' ? 'Total Default Payload' : 'Total Allocated Payload'}</span>
              <strong class="item-val-large">
                {(Number(detailAllocationItem.truck?.capacity || 17)) + (
                  trailerEditMode === 'new' ? (Number(newDetailTrailerCapacity) || 0) :
                  trailerEditMode === 'existing' ? (Number(availableTrailersList.find(t => t.id === selectedExistingTrailerId)?.capacity) || 0) :
                  editTrailer ? (Number(editTrailer.capacity) || 0) : 0
                )}t
              </strong>
            </div>
          </div>
          <p class="summary-note">
            {#if detailModalMode === 'truck-default'}
              Saving will update the default driver and trailer settings for this vehicle in your fleet.
            {:else}
              Note: If this truck does not have default values set, these driver and trailer details will automatically save as its defaults.
            {/if}
          </p>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="modal-footer">
        <button 
          type="button" 
          class="btn-modal-cancel" 
          on:click={() => isDetailModalOpen = false}
          disabled={isSavingDetail}
        >
          Cancel
        </button>
        <button 
          type="button" 
          class="btn-modal-save" 
          on:click={saveTruckDetailModal}
          disabled={isSavingDetail}
        >
          {#if isSavingDetail}
            <span class="loading-spin-small"></span>
            <span>Saving...</span>
          {:else}
            <Checkmark size={15} />
            <span>Save</span>
          {/if}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .truck-allocation-board {
    margin-top: 20px;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 12px;
    padding: 18px 20px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    font-family: var(--tt-font, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif);
  }

  /* Header */
  .allocation-board-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .badge-title-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    flex-wrap: wrap;
  }

  .board-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background-color: rgba(37, 99, 235, 0.1);
    color: var(--tt-primary, #2563eb);
    padding: 3px 8px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .board-title {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
  }

  .board-subtitle {
    margin: 0;
    font-size: 0.82rem;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.4;
    max-width: 780px;
  }

  /* Top Split: Left controls (Header, Schedule Params) & Right (Turnaround Schedule & Fleet Capacities) */
  .allocation-top-layout {
    display: grid;
    grid-template-columns: minmax(380px, 1fr) minmax(460px, 1.25fr);
    gap: 20px;
    align-items: start;
    margin-bottom: 20px;
    padding-bottom: 18px;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .allocation-top-left {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-width: 0;
  }

  .allocation-top-right {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 1180px) {
    .allocation-top-layout {
      grid-template-columns: 1fr;
      gap: 18px;
    }
  }

  /* Material Dispatch Info Card (Locations & Disposal Yard) */
  .material-dispatch-info-card {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 14px;
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: var(--tt-radius-md, 8px);
    margin: 2px 0 0 0;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  }

  .locations-row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  .location-box {
    flex: 1;
    min-width: 0;
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    transition: all 0.15s ease;
  }

  .location-box:hover {
    border-color: var(--tt-slate-300, #cbd5e1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .location-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .location-icon-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.65rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .load-badge {
    color: #047857;
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
  }

  .disposal-badge {
    color: #b45309;
    background-color: #fffbeb;
    border: 1px solid #fde68a;
  }

  .address-req-tag {
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding: 2px 6px;
    border-radius: 4px;
    background: #eff6ff;
    color: #2563eb;
    border: 1px solid #bfdbfe;
    white-space: nowrap;
  }

  .geocoder-embed-wrap {
    width: 100%;
    position: relative;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder) {
    width: 100% !important;
    max-width: 100% !important;
    border: 1px solid #cbd5e1 !important;
    border-radius: 6px !important;
    box-shadow: none !important;
    height: 34px !important;
    min-height: 34px !important;
    font-size: 12.5px !important;
    background-color: #ffffff !important;
    transition: all 0.15s ease !important;
    display: flex !important;
    align-items: center !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder:focus-within) {
    border-color: #2563eb !important;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15) !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--input) {
    height: 32px !important;
    padding: 0 28px 0 30px !important;
    font-size: 12px !important;
    color: #1e293b !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--icon-search) {
    top: 8px !important;
    left: 8px !important;
    fill: #94a3b8 !important;
  }

  :global(.geocoder-embed-wrap .mapboxgl-ctrl-geocoder--button) {
    top: 5px !important;
    right: 5px !important;
    background: transparent !important;
  }

  .address-geo-warning {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 500;
    color: #b91c1c;
    background: #fef2f2;
    border: 1px solid #fecaca;
    padding: 4px 8px;
    border-radius: 6px;
    margin-top: 3px;
    line-height: 1.25;
  }

  .route-arrow-divider {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  /* Disposal Yard Row */
  .yard-info-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding-top: 10px;
    border-top: 1px dashed var(--tt-slate-200, #e2e8f0);
    flex-wrap: wrap;
  }

  .yard-brand-group {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
    flex: 1 1 240px;
  }

  .yard-logo-container {
    width: 44px;
    height: 44px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    overflow: hidden;
    background: #ffffff;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    margin-top: 2px;
  }

  .yard-logo-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .yard-logo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    background: var(--tt-slate-100, #f1f5f9);
  }

  .yard-name-info {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 4px;
    flex: 1;
  }

  .yard-header-line {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .yard-badge-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--tt-slate-400, #94a3b8);
  }

  .yard-quick-select {
    font-size: 0.7rem;
    font-weight: 500;
    color: #334155;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 2px 6px;
    outline: none;
    max-width: 180px;
    cursor: pointer;
    transition: border-color 0.15s ease;
  }

  .yard-quick-select:focus {
    border-color: #2563eb;
  }

  .yard-inputs-row {
    display: flex;
    gap: 6px;
    width: 100%;
  }

  .site-detail-input {
    border: 1px solid #cbd5e1;
    border-radius: 5px;
    padding: 4px 8px;
    font-size: 0.75rem;
    color: #1e293b;
    background: #ffffff;
    outline: none;
    transition: all 0.15s ease;
  }

  .site-detail-input:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
  }

  .yard-name-input {
    flex: 1.4;
    font-weight: 600;
    min-width: 0;
  }

  .yard-suburb-input {
    flex: 1;
    min-width: 0;
  }

  .yard-live-link-box {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 3px 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    font-size: 11px;
    width: 100%;
    overflow: hidden;
  }

  .live-link-tag {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f1f5f9;
    color: #475569;
    padding: 2px 5px;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.3px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .live-link-tag.client-tag {
    background: rgba(37, 99, 235, 0.08);
    color: var(--tt-primary, #2563eb);
    border: 1px solid rgba(37, 99, 235, 0.2);
  }

  .live-pulse-dot {
    width: 6px;
    height: 6px;
    background-color: #22c55e;
    border-radius: 50%;
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    animation: livePulse 2s infinite;
    flex-shrink: 0;
  }

  @keyframes livePulse {
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
    }
    70% {
      transform: scale(1);
      box-shadow: 0 0 0 4px rgba(34, 197, 94, 0);
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
    }
  }

  .live-link-anchor {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: var(--tt-primary, #2563eb);
    text-decoration: none;
    font-size: 11px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .live-link-anchor:hover {
    text-decoration: underline;
  }

  .live-url-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .live-copy-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    background: #ffffff;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 10px;
    font-weight: 600;
    color: #475569;
    cursor: pointer;
    flex-shrink: 0;
    transition: all 0.15s ease;
  }

  .live-copy-btn:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #0f172a;
  }

  .live-copy-btn.copied {
    background: #dcfce7;
    border-color: #86efac;
    color: #15803d;
  }

  .client-live-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 6px 12px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 6px;
    font-size: 11.5px;
    overflow: hidden;
  }

  .yard-contact-card {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    padding: 6px 10px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 220px;
    flex: 1 1 220px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.02);
  }

  .contact-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .contact-badge-label {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #2563eb;
  }

  .contact-position-input {
    font-size: 0.65rem;
    padding: 2px 6px;
    max-width: 120px;
    color: #64748b;
  }

  .contact-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .contact-input-row {
    display: flex;
    align-items: center;
    gap: 6px;
    position: relative;
    width: 100%;
  }

  :global(.contact-input-icon) {
    color: #94a3b8;
    flex-shrink: 0;
  }

  .contact-field-input {
    flex: 1;
    min-width: 0;
    height: 26px;
  }

  .contact-sub-row {
    display: flex;
    gap: 6px;
    width: 100%;
  }

  .contact-phone-wrap,
  .contact-email-wrap {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 600px) {
    .locations-row {
      flex-direction: column;
      align-items: stretch;
    }

    .route-arrow-divider {
      transform: rotate(90deg);
      padding: 2px 0;
    }
  }



  /* Daily Columns Board */
  .daily-columns-container {
    display: flex;
    gap: 14px;
    overflow-x: auto;
    padding-bottom: 8px;
    scrollbar-width: thin;
  }

  .day-column {
    flex: 1;
    min-width: 250px;
    max-width: 320px;
    background-color: var(--tt-slate-50, #f8fafc);
    border: 2px dashed var(--tt-slate-200, #e2e8f0);
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    transition: all 0.2s ease;
  }

  .day-column.drop-target-active {
    border-color: var(--tt-primary, #2563eb);
    background-color: rgba(37, 99, 235, 0.05);
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
  }

  .day-column.drop-target-conflict {
    border-color: #ef4444 !important;
    background-color: rgba(254, 226, 226, 0.4) !important;
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3) !important;
  }

  /* Allocation Conflict Alert Banner (Board Level) */
  .allocation-conflict-alert {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 18px;
    background: linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%);
    border: 1px solid #f87171;
    border-left: 5px solid #dc2626;
    border-radius: 8px;
    margin: 12px 0 16px 0;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.12);
    z-index: 10;
  }

  .conflict-alert-icon {
    color: #dc2626;
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  .conflict-alert-body {
    flex: 1;
    min-width: 0;
  }

  .conflict-alert-title {
    font-size: 0.88rem;
    font-weight: 700;
    color: #991b1b;
    margin-bottom: 2px;
  }

  .conflict-alert-desc {
    font-size: 0.82rem;
    color: #7f1d1d;
    line-height: 1.4;
  }

  .conflict-alert-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  .conflict-view-job-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #dc2626;
    color: #ffffff;
    border: none;
    border-radius: 6px;
    padding: 7px 14px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3);
    transition: all 0.15s ease;
  }

  .conflict-view-job-btn:hover {
    background: #b91c1c;
    box-shadow: 0 3px 8px rgba(185, 28, 28, 0.4);
    transform: translateY(-1px);
  }

  .conflict-dismiss-btn {
    background: transparent;
    border: none;
    color: #991b1b;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s;
  }

  .conflict-dismiss-btn:hover {
    background: rgba(153, 27, 27, 0.1);
  }

  /* Drawer-level Conflict Alert */
  .drawer-conflict-alert {
    background: #fff1f2;
    border: 1px solid #fca5a5;
    border-left: 4px solid #dc2626;
    border-radius: 6px;
    padding: 10px 12px;
    margin-top: 10px;
    margin-bottom: 6px;
  }

  .drawer-conflict-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
  }

  .drawer-conflict-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 700;
    color: #991b1b;
  }

  .drawer-conflict-icon {
    color: #dc2626;
  }

  .drawer-conflict-dismiss {
    background: none;
    border: none;
    color: #991b1b;
    cursor: pointer;
    padding: 2px;
    display: flex;
  }

  .drawer-conflict-msg {
    font-size: 0.76rem;
    color: #7f1d1d;
    line-height: 1.35;
    margin: 0 0 8px 0;
  }

  .drawer-conflict-view-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #dc2626;
    color: #fff;
    border: none;
    border-radius: 4px;
    padding: 5px 10px;
    font-size: 0.76rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .drawer-conflict-view-btn:hover {
    background: #b91c1c;
  }

  .day-col-header {
    background-color: #ffffff;
    padding: 10px 12px;
    border-bottom: 1px solid var(--tt-slate-200, #e2e8f0);
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .day-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .day-badge {
    font-size: 0.85rem;
    font-weight: 800;
    color: var(--tt-slate-900);
  }

  .day-date {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--tt-slate-500);
  }

  .day-metrics-row {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .truck-count-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.72rem;
    font-weight: 600;
    background-color: rgba(37, 99, 235, 0.1);
    color: var(--tt-primary, #2563eb);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .cap-chip {
    font-size: 0.7rem;
    font-weight: 700;
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-600);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .day-actions-group {
    margin-left: auto;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .copy-day-btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--tt-slate-600, #475569);
    background: #ffffff;
    border: 1px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 4px;
    padding: 1px 5px;
    cursor: pointer;
    line-height: 1.4;
    transition: all 0.15s ease;
  }

  .copy-day-btn:hover {
    color: var(--tt-primary, #2563eb);
    border-color: var(--tt-primary, #2563eb);
    background: #eff6ff;
  }

  .clear-day-btn {
    border: none;
    background: transparent;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .clear-day-btn:hover {
    color: #ef4444;
    background-color: #fee2e2;
  }

  .day-col-body {
    flex: 1;
    min-height: 220px;
    padding: 10px;
    display: flex;
    flex-direction: column;
  }

  .empty-drop-zone {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 30px 10px;
    color: var(--tt-slate-400);
    text-align: center;
    gap: 4px;
    cursor: pointer;
    border-radius: 8px;
    transition: background-color 0.15s ease;
  }

  .empty-drop-zone:hover {
    background-color: rgba(37, 99, 235, 0.04);
  }

  .empty-drop-zone * {
    pointer-events: none;
  }

  :global(.drop-icon) {
    opacity: 0.5;
    margin-bottom: 4px;
  }

  .drop-prompt {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--tt-slate-500);
  }

  .drop-sub {
    font-size: 0.7rem;
    color: var(--tt-slate-400);
  }

  /* Scheduled Truck Cards */
  .allocated-trucks-stack {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .scheduled-truck-card {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200);
    border-radius: 8px;
    padding: 8px 10px;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    transition: all 0.15s ease;
    user-select: none;
    cursor: pointer;
  }

  .scheduled-truck-card.has-fleet-conflict {
    border-color: #ef4444 !important;
    background-color: #fffafb !important;
    box-shadow: 0 0 0 1px #ef4444, 0 1px 3px rgba(239, 68, 68, 0.15) !important;
  }

  .scheduled-truck-card.is-drag-target {
    border-color: var(--tt-primary, #2563eb);
    background-color: #eff6ff;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.35);
    transform: translateY(-1px);
  }

  .scheduled-truck-card:hover {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  .card-conflict-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 3px 0 5px 0;
    padding: 2px 6px;
    background: #fee2e2;
    border: 1px solid #f87171;
    border-radius: 4px;
    color: #991b1b;
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    max-width: 100%;
  }

  .card-conflict-pill:hover {
    background: #fecaca;
    color: #7f1d1d;
    border-color: #ef4444;
  }

  :global(.card-conflict-pill .conflict-warn-icon) {
    color: #dc2626 !important;
    flex-shrink: 0;
  }

  .conflict-job-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .stack-bottom-drop-zone {
    margin-top: 2px;
    padding: 7px 10px;
    border: 1.5px dashed var(--tt-slate-300, #cbd5e1);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.5);
    transition: all 0.15s ease;
    user-select: none;
    cursor: copy;
    min-height: 30px;
  }

  .stack-bottom-drop-zone:hover,
  .stack-bottom-drop-zone.is-active {
    border-color: var(--tt-primary, #2563eb);
    background-color: #eff6ff;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
  }

  .bottom-drop-text {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-slate-400, #94a3b8);
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color 0.15s ease;
    pointer-events: none;
  }

  .stack-bottom-drop-zone:hover .bottom-drop-text,
  .stack-bottom-drop-zone.is-active .bottom-drop-text {
    color: var(--tt-primary, #2563eb);
  }

  .scheduled-card-content-col {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 6px;
  }

  .truck-card-time-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
  }

  .scheduled-time-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background-color: #1e293b;
    color: #f8fafc;
    padding: 2px 7px;
    border-radius: 4px;
    font-size: 0.74rem;
    letter-spacing: 0.02em;
  }

  .sequence-tag {
    font-size: 0.68rem;
    font-weight: 700;
    color: var(--tt-slate-500);
    background-color: var(--tt-slate-100);
    padding: 1px 5px;
    border-radius: 4px;
  }

  .first-tag {
    background-color: #dcfce7;
    color: #15803d;
  }

  .remove-truck-btn {
    border: none;
    background: transparent;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 2px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .remove-truck-btn:hover {
    color: #ef4444;
    background-color: #fee2e2;
  }

  .truck-card-main {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .truck-id-line {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .truck-rego {
    font-size: 0.85rem;
    font-weight: 800;
    color: var(--tt-slate-900);
  }

  .truck-type-lbl {
    font-size: 0.68rem;
    color: var(--tt-slate-500);
    background-color: var(--tt-slate-100);
    padding: 1px 4px;
    border-radius: 3px;
  }

  .truck-capacity-lbl {
    font-size: 0.7rem;
    font-weight: 700;
    color: var(--tt-primary, #2563eb);
    margin-left: auto;
  }

  .driver-line {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
    font-size: 0.72rem;
    color: var(--tt-slate-600);
    margin-top: 2px;
  }

  .driver-details-wrap {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px 8px;
    min-width: 0;
    flex: 1;
  }

  .driver-info-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .driver-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: 500;
  }

  :global(.driver-icon) {
    color: var(--tt-slate-400);
    flex-shrink: 0;
  }

  .truck-card-bottom-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 4px;
    border-top: 1px dashed var(--tt-slate-100, #f1f5f9);
    margin-top: 2px;
  }

  .day-nav-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .card-edit-icon-btn {
    margin-left: auto;
    flex-shrink: 0;
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    color: var(--tt-primary, #2563eb);
    padding: 2px;
    border-radius: 4px;
    cursor: pointer;
    opacity: 0.85;
    transition: all 0.15s ease;
  }

  .card-edit-icon-btn:hover {
    opacity: 1;
    background-color: #eff6ff;
    color: #1d4ed8;
    transform: scale(1.12);
  }

  .move-day-btn {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    font-size: 0.65rem;
    font-weight: 600;
    color: var(--tt-slate-500);
    background: transparent;
    border: 1px solid var(--tt-slate-200);
    border-radius: 3px;
    padding: 1px 4px;
    cursor: pointer;
  }

  .move-day-btn:hover {
    color: var(--tt-primary);
    border-color: var(--tt-primary);
  }

  /* Slide-Out Fleet Drawer on Right Side (Unobstructed Side Panel) */
  .fleet-drawer-panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 420px;
    max-width: 92vw;
    height: 100vh;
    background-color: #ffffff;
    box-shadow: -6px 0 25px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 1200;
    border-left: 1px solid var(--tt-slate-200);
  }

  .drawer-header {
    padding: 16px 18px 12px 18px;
    border-bottom: 1px solid var(--tt-slate-200);
    background-color: #ffffff;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .drawer-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .drawer-heading-group {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  :global(.truck-drawer-icon) {
    color: var(--tt-primary, #2563eb);
  }

  .drawer-heading {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: var(--tt-slate-900);
  }

  .drawer-close-btn {
    border: none;
    background: transparent;
    color: var(--tt-slate-400);
    cursor: pointer;
    padding: 4px;
    border-radius: 50%;
  }

  .drawer-close-btn:hover {
    background-color: var(--tt-slate-100);
    color: var(--tt-slate-700);
  }

  .drawer-instruction {
    margin: 0;
    font-size: 0.78rem;
    color: var(--tt-slate-500);
    line-height: 1.4;
  }

  .drawer-search-box {
    display: flex;
    align-items: center;
    background-color: var(--tt-slate-50);
    border: 1px solid var(--tt-slate-300);
    border-radius: 6px;
    padding: 5px 8px;
    gap: 6px;
  }

  :global(.search-ico) {
    color: var(--tt-slate-400);
  }

  .drawer-search-input {
    border: none;
    background: transparent;
    font-size: 0.82rem;
    outline: none;
    width: 100%;
    color: var(--tt-slate-800);
  }

  .clear-search-btn {
    border: none;
    background: transparent;
    color: var(--tt-slate-400);
    cursor: pointer;
    font-size: 1rem;
    padding: 0 4px;
  }

  .drawer-filters {
    display: flex;
    gap: 6px;
    overflow-x: auto;
  }

  .type-pill {
    padding: 3px 8px;
    font-size: 0.72rem;
    font-weight: 600;
    border-radius: 9999px;
    border: 1px solid var(--tt-slate-200);
    background-color: var(--tt-slate-50);
    color: var(--tt-slate-600);
    cursor: pointer;
    white-space: nowrap;
  }

  .type-pill:hover {
    border-color: var(--tt-primary);
  }

  .type-pill.active {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: var(--tt-primary, #2563eb);
  }

  /* Drawer Add Truck Bar */
  .drawer-add-action-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding-top: 4px;
  }

  .add-truck-toggle-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: #ffffff;
    border: none;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.25);
    transition: all 0.15s ease;
  }

  .add-truck-toggle-btn:hover {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    transform: translateY(-1px);
    box-shadow: 0 3px 6px rgba(37, 99, 235, 0.35);
  }

  .add-truck-toggle-btn.active {
    background: var(--tt-slate-100);
    color: var(--tt-slate-700);
    border: 1px solid var(--tt-slate-300);
    box-shadow: none;
  }

  .recent-truck-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.74rem;
    color: #059669;
    background-color: #ecfdf5;
    border: 1px solid #a7f3d0;
    padding: 3px 8px;
    border-radius: 12px;
    font-weight: 600;
  }

  /* Add Truck Form Panel */
  .add-truck-form-panel {
    background: #f8fafc;
    border: 1px solid var(--tt-slate-200);
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.03);
  }

  .add-form-title-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--tt-slate-800);
  }

  .add-form-fields {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .add-form-row {
    display: flex;
    gap: 8px;
  }

  .add-field-group {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .add-field-group label {
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tt-slate-600);
  }

  .add-field-group .req {
    color: #ef4444;
  }

  .field-half { flex: 1; }
  .field-third { flex: 1; }
  .field-two-thirds { flex: 2; }
  .field-full { flex: 1; }

  .add-form-input {
    width: 100%;
    padding: 5px 8px;
    font-size: 0.8rem;
    border: 1px solid var(--tt-slate-300);
    border-radius: 5px;
    background: #ffffff;
    color: var(--tt-slate-800);
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
  }

  .add-form-input:focus {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .text-uppercase {
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.03em;
  }

  /* Trailer section in add truck form */
  .add-trailer-toggle-row {
    margin-top: 4px;
    padding-top: 6px;
    border-top: 1px dashed var(--tt-slate-200, #e2e8f0);
  }

  .trailer-toggle-label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
  }

  .trailer-checkbox {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: var(--tt-primary, #2563eb);
  }

  .trailer-toggle-text {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .trailer-subform-box {
    background: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-left: 3px solid #3b82f6;
    border-radius: 6px;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 4px;
  }

  .trailer-subform-title {
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #2563eb;
  }

  .combined-preview-cell {
    justify-content: center;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 6px;
    padding: 6px 8px;
  }

  .combined-label {
    font-size: 0.68rem;
    font-weight: 600;
    color: #166534;
    text-transform: uppercase;
  }

  .combined-value {
    font-size: 0.95rem;
    color: #15803d;
    display: flex;
    align-items: baseline;
    gap: 4px;
  }

  .combined-value small {
    font-size: 0.72rem;
    color: #166534;
    font-weight: normal;
  }

  .add-form-error {
    font-size: 0.74rem;
    color: #dc2626;
    background: #fef2f2;
    padding: 5px 8px;
    border-radius: 4px;
    border: 1px solid #fecaca;
  }

  .add-form-btn-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 4px;
  }

  .btn-cancel-add {
    padding: 5px 10px;
    font-size: 0.76rem;
    font-weight: 500;
    background: transparent;
    border: 1px solid var(--tt-slate-300);
    border-radius: 5px;
    color: var(--tt-slate-600);
    cursor: pointer;
  }

  .btn-cancel-add:hover {
    background: var(--tt-slate-100);
    color: var(--tt-slate-800);
  }

  .btn-submit-add {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 5px 12px;
    font-size: 0.76rem;
    font-weight: 600;
    background: #2563eb;
    border: none;
    border-radius: 5px;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .btn-submit-add:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .btn-submit-add:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .loading-spin-small {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    border-top-color: #ffffff;
    animation: spin 0.8s linear infinite;
  }

  /* Draggable Trucks List inside drawer */
  .drawer-trucks-list {
    flex: 1;
    overflow-y: auto;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background-color: var(--tt-slate-50);
  }

  .draggable-truck-card {
    background-color: #ffffff;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-left: 4px solid var(--tt-slate-300, #cbd5e1);
    border-radius: 10px;
    padding: 8px 10px;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  }

  .draggable-truck-card.card-just-added {
    border-left-color: #2563eb !important;
    border-color: #93c5fd !important;
    background-color: #eff6ff !important;
    animation: pulse-border 1.5s ease-in-out infinite;
  }

  @keyframes pulse-border {
    0%, 100% {
      box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.4);
    }
    50% {
      box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.2);
    }
  }

  .draggable-truck-card:hover {
    border-color: var(--tt-primary, #2563eb);
    border-left-color: var(--tt-primary, #2563eb);
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.1);
    transform: translateY(-1px);
  }

  .draggable-truck-card:active {
    transform: translateY(0);
  }

  .draggable-truck-card.card-scheduled {
    border-left-color: #10b981;
    background-color: #ffffff;
  }

  /* Drag handle column on the left */
  .card-drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 6px;
    border-radius: 6px;
    color: var(--tt-slate-400, #94a3b8);
    cursor: grab;
    flex-shrink: 0;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .card-drag-handle:hover {
    color: var(--tt-primary, #2563eb);
    background-color: rgba(37, 99, 235, 0.08);
    cursor: grab;
  }

  .card-drag-handle:active {
    cursor: grabbing;
  }

  .drag-dots-col {
    display: grid;
    grid-template-columns: repeat(2, 2.5px);
    grid-auto-rows: 2.5px;
    gap: 3.5px 3px;
    align-content: center;
    pointer-events: none;
  }

  .drag-dots-col .dot {
    width: 2.5px;
    height: 2.5px;
    background-color: currentColor;
    border-radius: 0.5px;
  }

  /* Right column for card content */
  .card-content-col {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    gap: 6px;
    justify-content: space-between;
  }

  /* Row 1: Vehicle & Driver Identity */
  .card-identity-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .identity-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
    min-width: 0;
  }

  .truck-rego-tag {
    font-size: 0.92rem;
    font-weight: 800;
    color: var(--tt-slate-900, #0f172a);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: -0.02em;
    flex-shrink: 0;
  }

  .truck-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
    line-height: 1.2;
    flex-shrink: 0;
  }

  .type-badge {
    color: var(--tt-slate-700, #334155);
    background-color: var(--tt-slate-100, #f1f5f9);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
  }

  .cap-badge {
    color: #1d4ed8;
    background-color: #eff6ff;
    border: 1px solid #dbeafe;
    font-weight: 700;
  }

  .identity-driver {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.74rem;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 140px;
    flex-shrink: 1;
    background-color: #f8fafc;
    padding: 2px 7px;
    border-radius: 12px;
    border: 1px solid #f1f5f9;
  }

  :global(.driver-ico) {
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  .driver-name-text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* Trailer badge on draggable card */
  .card-trailer-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding: 3px 6px;
    background-color: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 5px;
    font-size: 0.72rem;
  }

  .trailer-badge {
    color: #0f172a;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .trailer-badge strong {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    color: #1e293b;
  }

  .trailer-breakdown {
    font-size: 0.68rem;
    color: var(--tt-slate-500, #64748b);
    font-weight: 600;
  }

  /* Trailer badge in day column scheduled card */
  .truck-trailer-line {
    margin-top: 3px;
  }

  .trailer-sub-badge {
    display: inline-block;
    font-size: 0.68rem;
    color: #1e40af;
    background-color: #eff6ff;
    border: 1px solid #dbeafe;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .trailer-sub-badge strong {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  /* Row 2: Action Toolbar */
  .card-action-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    padding-top: 7px;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .alloc-prompt {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    color: var(--tt-slate-400, #94a3b8);
    text-transform: uppercase;
    letter-spacing: 0.03em;
    flex-shrink: 0;
  }

  :global(.alloc-ico) {
    color: var(--tt-slate-400, #94a3b8);
  }

  .quick-days-pills {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }

  .day-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 0.72rem;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    transition: all 0.15s ease;
    user-select: none;
    white-space: nowrap;
    line-height: 1.2;
  }

  .pill-assigned {
    background-color: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }

  .pill-btn-remove {
    cursor: pointer;
  }

  .pill-btn-remove .pill-icon-hover {
    display: none;
  }

  .pill-btn-remove .pill-icon-normal {
    display: inline-flex;
    align-items: center;
  }

  .pill-btn-remove:hover {
    background-color: #fee2e2;
    color: #b91c1c;
    border-color: #fca5a5;
    box-shadow: 0 1px 3px rgba(239, 68, 68, 0.2);
  }

  .pill-btn-remove:hover .pill-icon-normal {
    display: none;
  }

  .pill-btn-remove:hover .pill-icon-hover {
    display: inline-flex;
    align-items: center;
  }

  .pill-btn-add {
    background-color: #ffffff;
    color: var(--tt-primary, #2563eb);
    border: 1px solid #bfdbfe;
    cursor: pointer;
  }

  .pill-btn-add:hover {
    background-color: var(--tt-primary, #2563eb);
    color: #ffffff;
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
  }

  .pill-conflict {
    background-color: #fee2e2;
    color: #991b1b;
    border: 1px dashed #f87171;
    cursor: not-allowed;
    opacity: 0.85;
    text-decoration: line-through;
    position: relative;
  }

  .pill-conflict:hover {
    background-color: #fecaca;
    color: #7f1d1d;
    border-color: #ef4444;
    box-shadow: 0 1px 4px rgba(239, 68, 68, 0.25);
    opacity: 1;
  }

  .drawer-loading, .drawer-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 10px;
    color: var(--tt-slate-400);
    gap: 8px;
    font-size: 0.82rem;
    text-align: center;
  }

  .loading-spin {
    width: 20px;
    height: 20px;
    border: 2px solid var(--tt-slate-200);
    border-top-color: var(--tt-primary);
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* Interactive Scheduled Truck Card in Day Column */
  .interactive-card {
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .interactive-card:hover {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 3px 8px rgba(37, 99, 235, 0.12);
  }

  .driver-phone-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.68rem;
    color: var(--tt-slate-500, #64748b);
    white-space: nowrap;
    flex-shrink: 0;
    padding-left: 1px;
  }

  :global(.driver-phone-icon) {
    color: var(--tt-slate-400, #94a3b8);
    flex-shrink: 0;
  }

  .driver-phone-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    letter-spacing: -0.01em;
    white-space: nowrap;
  }


  /* Truck Allocation Detail Modal */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(15, 23, 42, 0.45);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    padding: 16px;
  }

  .truck-detail-modal-card {
    background-color: #ffffff;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    width: 100%;
    max-width: 540px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px 14px 20px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
    background-color: var(--tt-slate-50, #f8fafc);
  }

  .modal-title-group {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  :global(.modal-truck-icon) {
    color: var(--tt-primary, #2563eb);
    flex-shrink: 0;
  }

  .modal-title {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--tt-slate-900, #0f172a);
    line-height: 1.2;
  }

  .modal-subtitle {
    margin: 3px 0 0 0;
    font-size: 0.78rem;
    color: var(--tt-slate-500, #64748b);
  }

  .modal-close-btn {
    border: none;
    background: transparent;
    color: var(--tt-slate-400, #94a3b8);
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s ease;
  }

  .modal-close-btn:hover {
    color: var(--tt-slate-700, #334155);
    background-color: var(--tt-slate-200, #e2e8f0);
  }

  .modal-body {
    padding: 18px 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .detail-error-banner {
    background-color: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 0.8rem;
    padding: 8px 12px;
    border-radius: 6px;
    font-weight: 500;
  }

  .detail-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-bottom: 14px;
    border-bottom: 1px solid var(--tt-slate-100, #f1f5f9);
  }

  .section-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .section-title {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.88rem;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
  }

  .section-desc {
    margin: 0;
    font-size: 0.76rem;
    color: var(--tt-slate-500, #64748b);
  }

  .detail-grid-row {
    display: flex;
    gap: 12px;
  }

  .detail-input-group {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .detail-input-group label {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--tt-slate-700, #334155);
  }

  .detail-text-input,
  .detail-select-input {
    width: 100%;
    box-sizing: border-box;
    padding: 7px 10px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    font-size: 0.82rem;
    color: var(--tt-slate-900, #0f172a);
    background-color: #ffffff;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .detail-text-input:focus,
  .detail-select-input:focus {
    border-color: var(--tt-primary, #2563eb);
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  }

  .uppercase-input {
    text-transform: uppercase;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-weight: 600;
  }

  .action-align-end {
    justify-content: flex-end;
  }

  /* Current Trailer Display Card */
  .current-trailer-display {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 10px 14px;
    gap: 10px;
  }

  .current-trailer-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .trailer-pill-badge {
    font-size: 0.65rem;
    font-weight: 800;
    background-color: #16a34a;
    color: #ffffff;
    padding: 2px 6px;
    border-radius: 4px;
    letter-spacing: 0.04em;
  }

  .trailer-id-text {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.88rem;
    color: #14532d;
  }

  .trailer-type-tag {
    font-size: 0.72rem;
    color: #15803d;
    background-color: #dcfce7;
    padding: 1px 6px;
    border-radius: 4px;
  }

  .trailer-cap-tag {
    font-size: 0.72rem;
    font-weight: 700;
    color: #166534;
  }

  .btn-change-trailer,
  .btn-detach-trailer {
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
    border-radius: 4px;
    padding: 4px 8px;
    transition: all 0.15s ease;
  }

  .btn-change-trailer {
    background-color: #ffffff;
    border: 1px solid #86efac;
    color: #15803d;
  }

  .btn-change-trailer:hover {
    background-color: #dcfce7;
  }

  .btn-detach-trailer {
    background-color: transparent;
    border: 1px solid #fca5a5;
    color: #dc2626;
  }

  .btn-detach-trailer:hover {
    background-color: #fef2f2;
  }

  /* No Trailer State */
  .no-trailer-box {
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px dashed var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .no-trailer-text {
    font-size: 0.76rem;
    color: var(--tt-slate-500, #64748b);
  }

  .trailer-action-buttons {
    display: flex;
    gap: 8px;
  }

  .btn-trailer-mode {
    font-size: 0.74rem;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    background-color: #ffffff;
    color: var(--tt-slate-700, #334155);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-trailer-mode:hover {
    background-color: var(--tt-slate-100, #f1f5f9);
    border-color: var(--tt-slate-300, #cbd5e1);
  }

  .btn-trailer-mode.btn-mode-primary {
    background-color: #eff6ff;
    border-color: #bfdbfe;
    color: var(--tt-primary, #2563eb);
  }

  .btn-trailer-mode.btn-mode-primary:hover {
    background-color: #dbeafe;
  }

  /* Subforms for Trailer */
  .trailer-select-subform,
  .trailer-new-subform {
    background-color: var(--tt-slate-50, #f8fafc);
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 8px;
    padding: 12px 14px;
  }

  .subform-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.78rem;
    color: var(--tt-slate-800, #1e293b);
  }

  .btn-cancel-link {
    border: none;
    background: transparent;
    color: var(--tt-slate-400, #94a3b8);
    font-size: 0.72rem;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-cancel-link:hover {
    color: #ef4444;
  }

  .empty-hint {
    margin: 6px 0 0 0;
    font-size: 0.74rem;
    color: var(--tt-slate-500, #64748b);
  }

  .subform-footer-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
  }

  .btn-switch-mode {
    border: none;
    background: transparent;
    color: var(--tt-primary, #2563eb);
    font-size: 0.74rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }

  .btn-switch-mode:hover {
    text-decoration: underline;
  }

  /* Summary Card */
  .detail-summary-card {
    background: linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%);
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    padding: 12px 16px;
  }

  .summary-breakdown {
    display: flex;
    align-items: center;
    justify-content: space-around;
    gap: 8px;
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .item-label {
    font-size: 0.68rem;
    color: var(--tt-slate-500, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-weight: 600;
  }

  .item-val {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--tt-slate-800, #1e293b);
  }

  .calc-symbol {
    font-size: 1.1rem;
    font-weight: 700;
    color: var(--tt-slate-400, #94a3b8);
  }

  .total-item .item-label {
    color: var(--tt-primary, #2563eb);
  }

  .item-val-large {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--tt-primary, #2563eb);
  }

  .summary-note {
    margin: 8px 0 0 0;
    font-size: 0.7rem;
    color: var(--tt-slate-500, #64748b);
    line-height: 1.3;
    font-style: italic;
    border-top: 1px dashed #dbeafe;
    padding-top: 6px;
  }

  /* Modal Footer */
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid var(--tt-slate-100, #f1f5f9);
    background-color: var(--tt-slate-50, #f8fafc);
  }

  .btn-modal-cancel {
    padding: 7px 14px;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--tt-slate-600, #475569);
    background: transparent;
    border: 1px solid var(--tt-slate-200, #e2e8f0);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-modal-cancel:hover:not(:disabled) {
    background-color: var(--tt-slate-100, #f1f5f9);
    color: var(--tt-slate-900, #0f172a);
  }

  .btn-modal-save {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    font-size: 0.8rem;
    font-weight: 600;
    color: #ffffff;
    background-color: var(--tt-primary, #2563eb);
    border: 1px solid var(--tt-primary, #2563eb);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-modal-save:hover:not(:disabled) {
    background-color: #1d4ed8;
    border-color: #1d4ed8;
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
  }

  .btn-modal-save:disabled,
  .btn-modal-cancel:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
